import React from 'react';
import { CheckCircle2 } from 'lucide-react';

/* ═══════════════════════════════════════════════════
   12 UNIQUE PRODUCTS — Each with distinct visual identity
   Styles: Boho, Elegant, Playful, Modern, Dark, Minimal,
   Vintage, Cozy, Feminine, Scandi, Bold, Premium
   ═══════════════════════════════════════════════════ */

export const demoPages = {

  /* ─── 1. SOFT LIFE PLANNER — Feminine / Boho ─── */
  'Soft Life Planner': {
    genType: 'daily-planner', genTopic: 'soft life self care',
    includes: '25+ pages · Canva editable · Instant download',
    features: ['Daily routine planner', 'Self-care tracker', 'Wellness goals', 'Gratitude prompts', 'Monthly overview', 'Habit tracker'],
    faqs: [
      { q: 'How do I edit this product?', a: 'Fully editable in Canva. Customize colors, fonts, and text to match your brand.' },
      { q: 'Can I sell this on Etsy?', a: 'Yes! Includes a commercial license. Sell on Etsy, your website, or any platform.' },
    ],
    reviews: [
      { name: 'Emma R.', rating: 5, text: 'Beautiful design! My customers love it. Already made 15 sales in the first week.', date: '2 weeks ago' },
      { name: 'Sophie L.', rating: 5, text: 'So easy to customize in Canva. The layout is perfect for my audience.', date: '1 month ago' },
    ],
    pages: [
      ({ accent }) => (
        <div className="w-full h-full flex flex-col items-center justify-center text-center relative overflow-hidden" style={{ background: 'linear-gradient(155deg, #FAF5F0 0%, #F0E6D8 100%)' }}>
          <div className="absolute top-[15%] left-1/2 -translate-x-1/2 w-[60%] aspect-square rounded-full opacity-[0.06]" style={{ background: accent }} />
          <div className="absolute bottom-[10%] right-[10%] w-16 h-16 rounded-full opacity-[0.04]" style={{ background: accent }} />
          <div className="relative z-10 px-8">
            <p className="text-[9px] tracking-[0.4em] uppercase mb-5 font-sans" style={{ color: accent }}>New Edition</p>
            <h2 className="text-[26px] font-serif font-bold text-stone-800 leading-[1.15] mb-2">Soft Life<br/>Planner</h2>
            <div className="w-10 h-px mx-auto my-4" style={{ background: accent + '40' }} />
            <p className="text-[10px] font-sans text-stone-400 tracking-[0.15em]">Your guide to intentional living</p>
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#FFFCF9' }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xs font-serif font-bold text-stone-700">Daily Routine</h3>
            <span className="text-[8px] font-sans text-stone-400 tracking-wider uppercase">Monday</span>
          </div>
          <div className="space-y-2 flex-1">
            {['6:00 AM — Morning meditation','7:00 AM — Skincare routine','8:00 AM — Healthy breakfast','9:00 AM — Focus work block','12:00 PM — Mindful lunch','2:00 PM — Creative time','5:00 PM — Evening walk','8:00 PM — Journaling'].map((item, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded border-[1.5px] flex-shrink-0" style={{ borderColor: accent + '40' }} />
                <span className="text-[9px] font-sans text-stone-600 tracking-wide">{item}</span>
              </div>
            ))}
          </div>
          <div className="mt-3 pt-2 border-t" style={{ borderColor: accent + '15' }}>
            <p className="text-[8px] font-sans text-stone-400 italic">{"Today's affirmation: I deserve rest and joy."}</p>
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#FFFCF9' }}>
          <h3 className="text-xs font-serif font-bold text-stone-700 mb-4">Self-Care Tracker</h3>
          <div className="grid grid-cols-7 gap-1 mb-4">
            {['M','T','W','T','F','S','S'].map((d,i) => (<div key={i} className="text-center text-[7px] font-sans font-bold text-stone-400">{d}</div>))}
            {[...Array(28)].map((_,i) => (
              <div key={i} className="aspect-square rounded-md flex items-center justify-center" style={{ background: i < 18 ? accent + '10' : 'transparent', border: `1px solid ${accent}08` }}>
                {i < 18 && <CheckCircle2 className="w-2 h-2" style={{ color: accent }} />}
              </div>
            ))}
          </div>
          <div className="space-y-2 mt-auto">
            {['Water intake','Movement','Sleep quality','Mood'].map((item,i) => (
              <div key={i} className="flex items-center justify-between">
                <span className="text-[9px] font-sans text-stone-600">{item}</span>
                <div className="flex gap-0.5">{[...Array(5)].map((_,j) => (<div key={j} className="w-4 h-1.5 rounded-full" style={{ background: j < 4-i%2 ? accent : accent+'15' }} />))}</div>
              </div>
            ))}
          </div>
        </div>
      ),
    ],
  },

  /* ─── 2. WEDDING PLANNER — Elegant / Luxurious ─── */
  'Wedding Planner': {
    genType: 'daily-planner', genTopic: 'wedding planning checklist',
    includes: '30+ pages · Canva editable · Print ready',
    features: ['Wedding timeline', 'Budget tracker', 'Guest list manager', 'Seating chart', 'Vendor contacts', 'Day-of schedule'],
    faqs: [
      { q: 'Is this customizable for my wedding theme?', a: 'Absolutely! All colors, fonts, and layouts are fully editable in Canva to match your wedding aesthetic.' },
      { q: 'How many guests can I add?', a: 'Your planner adapts to your wedding. Enter your expected guest count and Lumera will automatically tailor your guest list, seating plan and other relevant planning tools to match.' },
    ],
    reviews: [
      { name: 'Isabella C.', rating: 5, text: 'This planner saved my sanity! Every detail is covered. Worth every penny.', date: '1 week ago' },
      { name: 'Charlotte B.', rating: 5, text: 'Gorgeous design. My bridesmaids all wanted their own copy!', date: '3 weeks ago' },
    ],
    pages: [
      ({ accent }) => (
        <div className="w-full h-full flex flex-col items-center justify-center text-center relative" style={{ background: '#FFFDF8' }}>
          <div className="absolute inset-5 border border-dashed opacity-20 rounded-lg" style={{ borderColor: accent }} />
          <div className="absolute inset-7 border opacity-10 rounded-lg" style={{ borderColor: accent }} />
          <div className="relative z-10 px-6">
            <p className="text-[8px] tracking-[0.5em] uppercase mb-5 font-sans" style={{ color: accent }}>Forever & Always</p>
            <h2 className="text-[24px] font-serif italic text-stone-800 leading-[1.2] mb-1">Wedding</h2>
            <h2 className="text-[28px] font-serif font-bold text-stone-800 leading-[1.1] mb-3">Planner</h2>
            <div className="flex items-center justify-center gap-2 my-4">
              <div className="w-6 h-px" style={{ background: accent + '50' }} />
              <div className="w-1.5 h-1.5 rotate-45" style={{ background: accent + '40' }} />
              <div className="w-6 h-px" style={{ background: accent + '50' }} />
            </div>
            <p className="text-[9px] font-sans text-stone-400 tracking-[0.2em]">Your dream wedding, organized</p>
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#FFFDF8' }}>
          <h3 className="text-xs font-serif font-bold text-stone-700 mb-1">Wedding Timeline</h3>
          <p className="text-[8px] font-sans text-stone-400 mb-4 tracking-wider uppercase">12 months to go</p>
          <div className="space-y-2.5 flex-1">
            {[{m:'12 months',t:'Book venue & photographer'},{m:'10 months',t:'Choose wedding party'},{m:'8 months',t:'Send save-the-dates'},{m:'6 months',t:'Book florist & caterer'},{m:'4 months',t:'Order invitations'},{m:'2 months',t:'Final dress fitting'},{m:'1 month',t:'Confirm all vendors'}].map((item,i) => (
              <div key={i} className="flex gap-3 items-start">
                <div className="flex flex-col items-center">
                  <div className="w-2 h-2 rounded-full" style={{ background: accent }} />
                  {i < 6 && <div className="w-px h-3" style={{ background: accent + '20' }} />}
                </div>
                <div>
                  <p className="text-[8px] font-sans font-bold" style={{ color: accent }}>{item.m}</p>
                  <p className="text-[9px] font-sans text-stone-600">{item.t}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#FFFDF8' }}>
          <h3 className="text-xs font-serif font-bold text-stone-700 mb-4">Guest List</h3>
          <div className="space-y-0 flex-1">
            <div className="grid grid-cols-[1fr_auto_auto] gap-x-3 pb-2 border-b" style={{ borderColor: accent+'15' }}>
              <span className="text-[7px] font-sans font-bold text-stone-400 uppercase tracking-wider">Name</span>
              <span className="text-[7px] font-sans font-bold text-stone-400 uppercase tracking-wider">RSVP</span>
              <span className="text-[7px] font-sans font-bold text-stone-400 uppercase tracking-wider">Table</span>
            </div>
            {[{n:'James & Sarah',r:'Yes',t:'1'},{n:'Michael & Emma',r:'Yes',t:'1'},{n:'David Chen',r:'Pending',t:'2'},{n:'Anna Williams',r:'Yes',t:'2'},{n:'The Johnsons (4)',r:'Yes',t:'3'},{n:'Maria Garcia',r:'No',t:'-'},{n:'Robert & Lisa',r:'Pending',t:'4'}].map((g,i) => (
              <div key={i} className="grid grid-cols-[1fr_auto_auto] gap-x-3 py-1.5 border-b border-stone-50">
                <span className="text-[9px] font-sans text-stone-700">{g.n}</span>
                <span className={`text-[8px] font-sans font-bold ${g.r==='Yes'?'text-emerald-500':g.r==='No'?'text-red-400':'text-amber-500'}`}>{g.r}</span>
                <span className="text-[9px] font-sans text-stone-400 text-center">{g.t}</span>
              </div>
            ))}
          </div>
          <div className="mt-3 pt-2 border-t flex justify-between" style={{ borderColor: accent+'15' }}>
            <span className="text-[8px] font-sans text-stone-400">Total: 24 guests</span>
            <span className="text-[8px] font-sans font-bold" style={{ color: accent }}>18 confirmed</span>
          </div>
        </div>
      ),
    ],
  },

  /* ─── 3. BABY MILESTONE BOOK — Soft / Playful ─── */
  'Baby Milestone Book': {
    genType: 'daily-planner', genTopic: 'baby first year milestone book',
    includes: '20+ pages · Pastel design · Printable keepsake',
    features: ['Monthly milestones', 'First moments tracker', 'Growth chart', 'Photo pages', 'Letter to baby', 'Family tree'],
    faqs: [
      { q: 'Can I add my own photos?', a: 'Yes! Every page has designated photo areas. Add photos in Canva or print and paste them in.' },
      { q: 'What age range does this cover?', a: 'From birth through the entire first year, with monthly milestone pages and special firsts.' },
    ],
    reviews: [
      { name: 'Hannah M.', rating: 5, text: 'The sweetest baby book! Perfect for new moms. Selling so well on my Etsy shop.', date: '4 days ago' },
      { name: 'Olivia K.', rating: 5, text: 'Love the pastel colors. My customers are obsessed with the milestone cards.', date: '2 weeks ago' },
    ],
    pages: [
      ({ accent }) => (
        <div className="w-full h-full flex flex-col items-center justify-center text-center relative overflow-hidden" style={{ background: 'linear-gradient(170deg, #FFF5F8 0%, #F0F4FF 60%, #FFF5F8 100%)' }}>
          {[{t:-8,l:15,s:10,c:'#FFD4E5'},{t:20,l:75,s:8,c:'#D4E5FF'},{t:55,l:10,s:6,c:'#FFE8D4'},{t:70,l:80,s:9,c:'#D4FFE5'},{t:85,l:45,s:7,c:'#E8D4FF'}].map((d,i) => (
            <div key={i} className="absolute rounded-full opacity-30" style={{ top:`${d.t}%`,left:`${d.l}%`,width:`${d.s}%`,height:`${d.s}%`,background:d.c }} />
          ))}
          <div className="relative z-10 px-6">
            <div className="text-[28px] mb-3">&#x2661;</div>
            <p className="text-[8px] tracking-[0.4em] uppercase mb-4 font-sans" style={{ color: accent }}>A keepsake for</p>
            <h2 className="text-[22px] font-sans font-extrabold text-stone-700 leading-[1.2] mb-1" style={{ fontFamily: 'system-ui' }}>My Baby&apos;s</h2>
            <h2 className="text-[26px] font-sans font-extrabold leading-[1.1] mb-3" style={{ color: accent, fontFamily: 'system-ui' }}>First Year</h2>
            <p className="text-[9px] font-sans text-stone-400 tracking-wide">Every precious moment, remembered</p>
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#FFFBFD' }}>
          <h3 className="text-xs font-sans font-extrabold text-stone-700 mb-1" style={{ fontFamily: 'system-ui' }}>Month 1</h3>
          <p className="text-[8px] font-sans text-stone-400 mb-4">First precious moments</p>
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div className="aspect-square rounded-2xl flex items-center justify-center text-[10px] font-sans text-stone-300" style={{ background: accent+'08', border: `1px dashed ${accent}25` }}>Photo</div>
            <div className="space-y-1.5">
              {[{l:'Weight',v:'7 lbs 4 oz'},{l:'Length',v:'20 inches'},{l:'First smile',v:'Day 12'}].map((s,i) => (
                <div key={i} className="p-1.5 rounded-lg" style={{ background: accent+'06' }}>
                  <p className="text-[7px] font-sans text-stone-400">{s.l}</p>
                  <p className="text-[9px] font-sans font-bold text-stone-700">{s.v}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="space-y-2 flex-1">
            <p className="text-[8px] font-sans font-bold text-stone-500 uppercase tracking-wider">Milestones</p>
            {['First bath','Umbilical cord fell off','Slept through the night','First real smile','Held their head up'].map((m,i) => (
              <div key={i} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full border-[1.5px] flex-shrink-0" style={{ borderColor: accent+'30', background: i < 3 ? accent+'15' : 'transparent' }} />
                <span className="text-[9px] font-sans text-stone-600">{m}</span>
              </div>
            ))}
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#FFFBFD' }}>
          <h3 className="text-xs font-sans font-extrabold text-stone-700 mb-4" style={{ fontFamily: 'system-ui' }}>Growth Chart</h3>
          <div className="flex-1 flex flex-col justify-end gap-1 mb-3">
            {[{m:'Month 1',w:7.4,h:50},{m:'Month 2',w:9.0,h:55},{m:'Month 3',w:11.2,h:60},{m:'Month 4',w:12.8,h:65},{m:'Month 5',w:14.1,h:70},{m:'Month 6',w:15.5,h:75}].map((d,i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-[7px] font-sans text-stone-400 w-12">{d.m}</span>
                <div className="flex-1 h-3 rounded-full overflow-hidden" style={{ background: accent+'08' }}>
                  <div className="h-full rounded-full" style={{ width: `${d.h}%`, background: `linear-gradient(90deg, ${accent}40, ${accent})` }} />
                </div>
                <span className="text-[7px] font-sans text-stone-500 w-10 text-right">{d.w} lbs</span>
              </div>
            ))}
          </div>
          <div className="pt-2 border-t border-stone-100 text-center">
            <p className="text-[8px] font-sans text-stone-400">Growing so fast! &#x2661;</p>
          </div>
        </div>
      ),
    ],
  },

  /* ─── 4. BUDGET TRACKER — Modern / Clean ─── */
  'Budget Tracker': {
    genType: 'daily-planner', genTopic: 'monthly budget tracker',
    includes: '20+ pages · Print ready · Works with any currency',
    features: ['Monthly budget overview', 'Expense categories', 'Savings tracker', 'Debt payoff plan', 'Financial goals', 'Income log'],
    faqs: [
      { q: 'Can I use this with any currency?', a: 'Absolutely! The template uses placeholder amounts that you can change to any currency.' },
      { q: 'Is this printable?', a: 'Yes! Optimized for A4 and US Letter. Also works digitally on iPad.' },
    ],
    reviews: [
      { name: 'Lisa M.', rating: 5, text: 'Finally a budget tracker that actually looks good! My customers keep coming back.', date: '3 weeks ago' },
      { name: 'Anna K.', rating: 4, text: 'Great template. Clean design and easy to customize.', date: '1 month ago' },
    ],
    pages: [
      ({ accent }) => (
        <div className="w-full h-full flex items-center relative overflow-hidden" style={{ background: '#F8FAF8' }}>
          <div className="absolute left-0 top-0 bottom-0 w-[6px]" style={{ background: accent }} />
          <div className="absolute right-[10%] top-[10%] w-[30%] aspect-square rounded-full opacity-[0.03]" style={{ background: accent }} />
          <div className="pl-8 pr-6 relative z-10">
            <p className="text-[9px] tracking-[0.35em] uppercase mb-4 font-sans font-bold" style={{ color: accent }}>Financial Goals</p>
            <h2 className="text-[30px] font-sans font-black text-stone-800 leading-[1.05] mb-2" style={{ fontFamily: 'system-ui' }}>Budget<br/>Tracker</h2>
            <p className="text-[10px] font-sans text-stone-400 tracking-wide">Take control of your money</p>
            <div className="mt-5 flex gap-1.5">
              {[...Array(4)].map((_,i) => <div key={i} className="w-3 h-1 rounded-full" style={{ background: accent, opacity: 1 - i*0.2 }} />)}
            </div>
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#FAFCFA' }}>
          <h3 className="text-xs font-sans font-bold text-stone-700 mb-1" style={{ fontFamily: 'system-ui' }}>Monthly Overview</h3>
          <p className="text-[8px] font-sans text-stone-400 mb-4 tracking-wider uppercase">Monthly Overview</p>
          <div className="grid grid-cols-2 gap-2 mb-4">
            {[{l:'Income',v:'$4,200',c:accent},{l:'Expenses',v:'$2,850',c:'#B85C5C'},{l:'Savings',v:'$1,350',c:accent},{l:'Goal',v:'$1,500',c:accent+'80'}].map((b,i) => (
              <div key={i} className="rounded-lg p-2.5 text-center" style={{ background: b.c+'08', border: `1px solid ${b.c}12` }}>
                <p className="text-[7px] font-sans text-stone-500 uppercase tracking-wider">{b.l}</p>
                <p className="text-sm font-sans font-bold mt-0.5" style={{ color: b.c }}>{b.v}</p>
              </div>
            ))}
          </div>
          <div className="space-y-1.5 flex-1">
            {[{cat:'Housing',pct:35},{cat:'Food',pct:20},{cat:'Transport',pct:12},{cat:'Savings',pct:25},{cat:'Other',pct:8}].map((e,i) => (
              <div key={i}>
                <div className="flex justify-between text-[8px] font-sans mb-0.5"><span className="text-stone-600">{e.cat}</span><span className="text-stone-400">{e.pct}%</span></div>
                <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden"><div className="h-full rounded-full" style={{ width: `${e.pct*2.5}%`, background: accent }} /></div>
              </div>
            ))}
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#FAFCFA' }}>
          <h3 className="text-xs font-sans font-bold text-stone-700 mb-4" style={{ fontFamily: 'system-ui' }}>Expense Log</h3>
          <div className="space-y-0 flex-1">
            {[{d:'Jan 3',n:'Grocery Store',a:'-$85.40'},{d:'Jan 5',n:'Netflix',a:'-$15.99'},{d:'Jan 7',n:'Gas Station',a:'-$42.00'},{d:'Jan 8',n:'Coffee Shop',a:'-$6.50'},{d:'Jan 10',n:'Rent',a:'-$1,200'},{d:'Jan 12',n:'Online Course',a:'-$29.99'}].map((e,i) => (
              <div key={i} className="flex items-center justify-between py-1.5 border-b border-stone-50">
                <div><p className="text-[9px] font-sans font-medium text-stone-700">{e.n}</p><p className="text-[7px] font-sans text-stone-400">{e.d}</p></div>
                <span className="text-[9px] font-sans font-bold text-stone-600">{e.a}</span>
              </div>
            ))}
          </div>
        </div>
      ),
    ],
  },

  /* ─── 5. FITNESS JOURNAL — Bold / Dark Mode ─── */
  'Fitness Journal': {
    genType: 'daily-planner', genTopic: 'fitness workout journal',
    includes: '22+ pages · Dark mode design · Gym-ready',
    features: ['Workout log', 'Exercise tracker', 'Progress photos', 'Meal plan', 'Body measurements', 'PR tracker'],
    faqs: [
      { q: 'Can I use this on my phone at the gym?', a: 'Yes! The digital version works perfectly on any device. Print it out for a gym binder too.' },
      { q: 'Are the workouts customizable?', a: 'Completely! Add your own exercises, sets, and reps. The template adapts to any workout style.' },
    ],
    reviews: [
      { name: 'Jake T.', rating: 5, text: 'The dark design looks amazing. My gym clients love tracking their progress with this.', date: '1 week ago' },
      { name: 'Alex M.', rating: 5, text: 'Finally a fitness tracker that looks as hard as my workouts. Selling great on Etsy.', date: '3 weeks ago' },
    ],
    pages: [
      ({ accent }) => (
        <div className="w-full h-full flex flex-col items-center justify-center text-center relative overflow-hidden" style={{ background: '#1A1A1A' }}>
          <div className="absolute top-[20%] right-[15%] w-[35%] aspect-square rounded-full opacity-[0.08]" style={{ background: accent }} />
          <div className="absolute bottom-0 left-0 right-0 h-[3px]" style={{ background: `linear-gradient(90deg, transparent, ${accent}, transparent)` }} />
          <div className="relative z-10 px-6">
            <p className="text-[9px] tracking-[0.4em] uppercase mb-5 font-sans font-bold" style={{ color: accent }}>Push Harder</p>
            <h2 className="text-[30px] font-sans font-black text-white leading-[1.05] mb-2" style={{ fontFamily: 'system-ui' }}>Fitness<br/>Journal</h2>
            <p className="text-[10px] font-sans text-stone-500 tracking-wide">Track. Train. Transform.</p>
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#1A1A1A' }}>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xs font-sans font-bold text-white" style={{ fontFamily: 'system-ui' }}>Workout Log</h3>
            <span className="text-[8px] font-sans px-2 py-0.5 rounded-full" style={{ background: accent+'20', color: accent }}>Upper Body</span>
          </div>
          <div className="space-y-1.5 flex-1">
            {[{ex:'Bench Press',s:'4×8',w:'135 lbs'},{ex:'Shoulder Press',s:'3×10',w:'65 lbs'},{ex:'Bent Over Row',s:'4×8',w:'95 lbs'},{ex:'Lateral Raise',s:'3×12',w:'20 lbs'},{ex:'Bicep Curls',s:'3×12',w:'30 lbs'},{ex:'Tricep Dips',s:'3×15',w:'BW'}].map((e,i) => (
              <div key={i} className="flex items-center justify-between py-1.5 px-2 rounded-lg" style={{ background: '#252525' }}>
                <span className="text-[9px] font-sans text-stone-300">{e.ex}</span>
                <div className="flex gap-3">
                  <span className="text-[8px] font-sans text-stone-500">{e.s}</span>
                  <span className="text-[8px] font-sans font-bold" style={{ color: accent }}>{e.w}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 pt-2 border-t border-stone-800 flex justify-between">
            <span className="text-[8px] font-sans text-stone-500">Duration: 55 min</span>
            <span className="text-[8px] font-sans font-bold" style={{ color: accent }}>Calories: 420</span>
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#1A1A1A' }}>
          <h3 className="text-xs font-sans font-bold text-white mb-4" style={{ fontFamily: 'system-ui' }}>Progress Tracker</h3>
          <div className="grid grid-cols-3 gap-2 mb-4">
            {[{l:'Weight',v:'175',u:'lbs'},{l:'Body Fat',v:'18',u:'%'},{l:'Muscle',v:'42',u:'%'}].map((s,i) => (
              <div key={i} className="p-2 rounded-lg text-center" style={{ background: '#252525' }}>
                <p className="text-[7px] font-sans text-stone-500 uppercase">{s.l}</p>
                <p className="text-sm font-sans font-black text-white">{s.v}<span className="text-[8px] text-stone-500 ml-0.5">{s.u}</span></p>
              </div>
            ))}
          </div>
          <div className="space-y-2 flex-1">
            <p className="text-[8px] font-sans text-stone-500 uppercase tracking-wider">Personal Records</p>
            {[{ex:'Deadlift',pr:'315 lbs',d:'+10'},{ex:'Squat',pr:'275 lbs',d:'+15'},{ex:'Bench',pr:'185 lbs',d:'+5'}].map((pr,i) => (
              <div key={i} className="flex items-center justify-between px-2 py-1.5 rounded-lg" style={{ background: '#252525' }}>
                <span className="text-[9px] font-sans text-stone-300">{pr.ex}</span>
                <div className="flex items-center gap-2">
                  <span className="text-[9px] font-sans font-bold text-white">{pr.pr}</span>
                  <span className="text-[7px] font-sans px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400">{pr.d}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ),
    ],
  },

  /* ─── 6. CONTENT PLANNER — Minimal / Professional ─── */
  'Content Planner': {
    genType: 'daily-planner', genTopic: 'social media content planner',
    includes: '18+ pages · Social media ready · Analytics tracking',
    features: ['Content calendar', 'Post ideas bank', 'Hashtag tracker', 'Analytics dashboard', 'Brand guidelines', 'Engagement tracker'],
    faqs: [
      { q: 'Which platforms does this cover?', a: 'Instagram, TikTok, Pinterest, Twitter, and YouTube. Each has dedicated planning sections.' },
      { q: 'Can I track analytics?', a: 'Yes! Includes weekly and monthly analytics tracking pages for all major metrics.' },
    ],
    reviews: [
      { name: 'Mia R.', rating: 5, text: 'Game changer for my content strategy. Love the minimalist design.', date: '5 days ago' },
      { name: 'Laura S.', rating: 4, text: 'Very professional looking. My social media agency clients approve!', date: '2 weeks ago' },
    ],
    pages: [
      ({ accent }) => (
        <div className="w-full h-full flex relative overflow-hidden" style={{ background: '#F7F8FA' }}>
          <div className="w-[45%] h-full flex items-center justify-center" style={{ background: accent }}>
            <div className="text-right pr-4">
              <p className="text-[8px] tracking-[0.4em] uppercase mb-2 font-sans text-white/60">Social Strategy</p>
              <h2 className="text-[20px] font-sans font-black text-white leading-[1.1]" style={{ fontFamily: 'system-ui' }}>Content</h2>
            </div>
          </div>
          <div className="flex-1 flex items-center pl-4">
            <div>
              <h2 className="text-[20px] font-sans font-black text-stone-800 leading-[1.1]" style={{ fontFamily: 'system-ui' }}>Planner</h2>
              <div className="w-8 h-0.5 mt-3 mb-2" style={{ background: accent }} />
              <p className="text-[9px] font-sans text-stone-400">Plan · Create · Grow</p>
            </div>
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#F7F8FA' }}>
          <h3 className="text-xs font-sans font-bold text-stone-700 mb-4" style={{ fontFamily: 'system-ui' }}>Weekly Calendar</h3>
          <div className="grid grid-cols-7 gap-1 flex-1">
            {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map((d,i) => (
              <div key={i} className="flex flex-col">
                <p className="text-[6px] font-sans font-bold text-stone-400 text-center mb-1 uppercase">{d}</p>
                <div className="flex-1 rounded-md p-1 space-y-0.5" style={{ background: i < 5 ? accent+'06' : 'transparent', border: `1px solid ${accent}08` }}>
                  {i < 5 && ['Reel','Story','Post'].slice(0, i%3+1).map((t,j) => (
                    <div key={j} className="text-[5px] font-sans px-1 py-0.5 rounded" style={{ background: accent+'15', color: accent }}>{t}</div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 pt-2 border-t border-stone-200 flex justify-between">
            <span className="text-[7px] font-sans text-stone-400">Week 1 · Content Calendar</span>
            <span className="text-[7px] font-sans font-bold" style={{ color: accent }}>12 posts planned</span>
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#F7F8FA' }}>
          <h3 className="text-xs font-sans font-bold text-stone-700 mb-4" style={{ fontFamily: 'system-ui' }}>Analytics</h3>
          <div className="grid grid-cols-2 gap-2 mb-4">
            {[{l:'Followers',v:'12.4K',d:'+2.3%'},{l:'Engagement',v:'4.8%',d:'+0.5%'},{l:'Reach',v:'45K',d:'+12%'},{l:'Saves',v:'890',d:'+18%'}].map((s,i) => (
              <div key={i} className="p-2 rounded-lg bg-white border border-stone-100">
                <p className="text-[7px] font-sans text-stone-400 uppercase">{s.l}</p>
                <div className="flex items-end gap-1.5">
                  <p className="text-sm font-sans font-black text-stone-800">{s.v}</p>
                  <span className="text-[7px] font-sans text-emerald-500 font-bold mb-0.5">{s.d}</span>
                </div>
              </div>
            ))}
          </div>
          <p className="text-[8px] font-sans font-bold text-stone-500 uppercase tracking-wider mb-2">Top performing posts</p>
          <div className="space-y-1.5 flex-1">
            {[{t:'10 Tips for Creators',e:'8.2%',r:'12K'},{t:'Behind the Scenes',e:'6.5%',r:'9.8K'},{t:'Product Launch Day',e:'5.9%',r:'8.1K'}].map((p,i) => (
              <div key={i} className="flex items-center justify-between py-1.5 px-2 bg-white rounded-lg border border-stone-100">
                <span className="text-[8px] font-sans text-stone-700">{p.t}</span>
                <span className="text-[7px] font-sans font-bold" style={{ color: accent }}>{p.e}</span>
              </div>
            ))}
          </div>
        </div>
      ),
    ],
  },

  /* ─── 7. TRAVEL JOURNAL — Vintage / Editorial ─── */
  'Travel Journal': {
    genType: 'daily-planner', genTopic: 'travel journal adventure planner',
    includes: '24+ pages · Vintage aesthetic · Trip planner',
    features: ['Trip planner', 'Packing checklist', 'Daily travel log', 'Bucket list', 'Expense tracker', 'Memory pages'],
    faqs: [
      { q: 'Can I use this for multiple trips?', a: 'Yes! The template has sections for multiple destinations. Duplicate pages in Canva for more trips.' },
      { q: 'Does it include a budget tracker?', a: 'Yes! There is a travel expense tracker page for each trip.' },
    ],
    reviews: [
      { name: 'Sophia V.', rating: 5, text: 'The vintage design is gorgeous. My travel-loving customers are obsessed!', date: '6 days ago' },
      { name: 'Lily T.', rating: 5, text: 'Perfect for travel bloggers and gift shops. Love the aesthetic.', date: '1 month ago' },
    ],
    pages: [
      ({ accent }) => (
        <div className="w-full h-full flex flex-col items-center justify-center text-center relative" style={{ background: '#F8F2E8' }}>
          <div className="absolute inset-4 border-2 rounded-sm" style={{ borderColor: accent+'25' }} />
          <div className="absolute top-3 left-3 w-3 h-3 border-t-2 border-l-2 rounded-tl-sm" style={{ borderColor: accent+'40' }} />
          <div className="absolute top-3 right-3 w-3 h-3 border-t-2 border-r-2 rounded-tr-sm" style={{ borderColor: accent+'40' }} />
          <div className="absolute bottom-3 left-3 w-3 h-3 border-b-2 border-l-2 rounded-bl-sm" style={{ borderColor: accent+'40' }} />
          <div className="absolute bottom-3 right-3 w-3 h-3 border-b-2 border-r-2 rounded-br-sm" style={{ borderColor: accent+'40' }} />
          <div className="relative z-10 px-8">
            <p className="text-[8px] tracking-[0.5em] uppercase mb-5 font-sans" style={{ color: accent }}>Adventure Awaits</p>
            <h2 className="text-[26px] font-serif font-bold text-stone-800 leading-[1.15] mb-1">Travel</h2>
            <h2 className="text-[26px] font-serif italic text-stone-800 leading-[1.1] mb-3">Journal</h2>
            <div className="text-[16px] my-3" style={{ color: accent }}>&#x2708;</div>
            <p className="text-[9px] font-sans text-stone-400 tracking-[0.15em]">Collect moments, not things</p>
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#FBF7F0' }}>
          <h3 className="text-xs font-serif font-bold text-stone-700 mb-1">Trip Planner</h3>
          <p className="text-[8px] font-sans text-stone-400 mb-4">Tokyo, Japan · 5-Day Itinerary</p>
          <div className="space-y-2 flex-1">
            {[{d:'Day 1',p:'Shibuya & Harajuku',n:'Explore street fashion, visit Meiji Shrine'},{d:'Day 2',p:'Asakusa & Akihabara',n:'Senso-ji temple, anime culture'},{d:'Day 3',p:'Mt. Fuji Day Trip',n:'Scenic train, lake views'},{d:'Day 4',p:'Tsukiji & Ginza',n:'Fish market, luxury shopping'},{d:'Day 5',p:'Shinjuku & Departure',n:'Golden Gai, last ramen bowl'}].map((item,i) => (
              <div key={i} className="flex gap-2.5">
                <div className="w-10 text-center flex-shrink-0">
                  <span className="text-[7px] font-sans font-bold px-1.5 py-0.5 rounded" style={{ background: accent+'15', color: accent }}>{item.d}</span>
                </div>
                <div className="flex-1 border-l pl-2" style={{ borderColor: accent+'20' }}>
                  <p className="text-[9px] font-sans font-bold text-stone-700">{item.p}</p>
                  <p className="text-[7px] font-sans text-stone-400">{item.n}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#FBF7F0' }}>
          <h3 className="text-xs font-serif font-bold text-stone-700 mb-4">Packing Checklist</h3>
          <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 flex-1">
            {['Passport','Travel adapter','Comfortable shoes','Rain jacket','Camera','Guidebook','Snacks','Sunscreen','Headphones','Journal','Medications','Cash (¥)'].map((item,i) => (
              <div key={i} className="flex items-center gap-1.5 py-1">
                <div className="w-2.5 h-2.5 rounded border flex-shrink-0" style={{ borderColor: accent+'30', background: i < 7 ? accent+'12' : 'transparent' }}>
                  {i < 7 && <CheckCircle2 className="w-2.5 h-2.5" style={{ color: accent }} />}
                </div>
                <span className={`text-[8px] font-sans ${i < 7 ? 'text-stone-400 line-through' : 'text-stone-600'}`}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      ),
    ],
  },

  /* ─── 8. RECIPE BOOK — Warm / Cozy ─── */
  'Recipe Book': {
    genType: 'ebook', genTopic: 'personal recipe book cookbook',
    includes: '28+ pages · Kitchen-ready design · Category dividers',
    features: ['Recipe cards', 'Ingredient lists', 'Cooking notes', 'Category dividers', 'Shopping list', 'Conversion chart'],
    faqs: [
      { q: 'How many recipes can I add?', a: 'The template comes with 28+ pages. Duplicate recipe card pages in Canva to add as many as you want.' },
      { q: 'Can I add my own photos?', a: 'Absolutely! Each recipe card has a designated photo area for food photography.' },
    ],
    reviews: [
      { name: 'Nina F.', rating: 5, text: 'The terracotta color palette is gorgeous. Customers say it feels like a real cookbook!', date: '1 week ago' },
      { name: 'Rose B.', rating: 5, text: 'So cozy and warm. The recipe card layout is perfect. My best-selling product.', date: '2 weeks ago' },
    ],
    pages: [
      ({ accent }) => (
        <div className="w-full h-full flex flex-col items-center justify-center text-center relative overflow-hidden" style={{ background: 'linear-gradient(160deg, #FEF6F0 0%, #F8EDE4 100%)' }}>
          <div className="absolute top-[5%] left-[5%] w-[40%] h-[40%] rounded-[40%_60%_55%_45%] opacity-[0.06]" style={{ background: accent }} />
          <div className="absolute bottom-[10%] right-[5%] w-[30%] h-[30%] rounded-[55%_45%_50%_50%] opacity-[0.04]" style={{ background: accent }} />
          <div className="relative z-10 px-6">
            <p className="text-[8px] tracking-[0.4em] uppercase mb-5 font-sans" style={{ color: accent }}>Kitchen Stories</p>
            <h2 className="text-[20px] font-serif italic text-stone-700 leading-[1.3]">My</h2>
            <h2 className="text-[28px] font-serif font-bold text-stone-800 leading-[1.1] mb-3">Recipe Book</h2>
            <div className="flex items-center justify-center gap-1.5 my-3">
              <div className="w-5 h-px" style={{ background: accent+'40' }} />
              <span className="text-[14px]" style={{ color: accent }}>&#x273B;</span>
              <div className="w-5 h-px" style={{ background: accent+'40' }} />
            </div>
            <p className="text-[9px] font-sans text-stone-400 tracking-wide">A collection of family favorites</p>
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#FFFAF5' }}>
          <h3 className="text-xs font-serif font-bold text-stone-700 mb-1">Lemon Herb Pasta</h3>
          <p className="text-[8px] font-sans text-stone-400 mb-3">Prep: 10 min · Cook: 15 min · Serves 4</p>
          <div className="aspect-[2/1] rounded-xl mb-3 flex items-center justify-center text-[9px] font-sans text-stone-300" style={{ background: accent+'06', border: `1px dashed ${accent}20` }}>Photo</div>
          <div className="grid grid-cols-2 gap-3 flex-1">
            <div>
              <p className="text-[7px] font-sans font-bold text-stone-500 uppercase tracking-wider mb-1.5">Ingredients</p>
              {['400g spaghetti','2 lemons, juiced','Fresh herbs','3 cloves garlic','Olive oil','Parmesan'].map((i,idx) => (
                <div key={idx} className="flex items-start gap-1 mb-0.5">
                  <span className="text-[6px] mt-0.5" style={{ color: accent }}>&#x25CF;</span>
                  <span className="text-[8px] font-sans text-stone-600">{i}</span>
                </div>
              ))}
            </div>
            <div>
              <p className="text-[7px] font-sans font-bold text-stone-500 uppercase tracking-wider mb-1.5">Steps</p>
              {['Boil pasta al dente','Sauté garlic in oil','Add lemon & herbs','Toss with pasta','Top with parmesan'].map((s,idx) => (
                <div key={idx} className="flex items-start gap-1 mb-0.5">
                  <span className="text-[7px] font-sans font-bold flex-shrink-0" style={{ color: accent }}>{idx+1}.</span>
                  <span className="text-[8px] font-sans text-stone-600">{s}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#FFFAF5' }}>
          <h3 className="text-xs font-serif font-bold text-stone-700 mb-4">Recipe Index</h3>
          <div className="space-y-3 flex-1">
            {[{cat:'Breakfast',items:['Avocado Toast','Fluffy Pancakes','Smoothie Bowl']},{cat:'Main Courses',items:['Lemon Herb Pasta','Chicken Stir Fry','Veggie Tacos']},{cat:'Desserts',items:['Chocolate Brownies','Lemon Bars','Berry Crumble']}].map((c,i) => (
              <div key={i}>
                <p className="text-[8px] font-sans font-bold uppercase tracking-wider mb-1.5" style={{ color: accent }}>{c.cat}</p>
                {c.items.map((item,j) => (
                  <div key={j} className="flex items-center justify-between py-1 border-b border-stone-50">
                    <span className="text-[9px] font-sans text-stone-600">{item}</span>
                    <span className="text-[7px] font-sans text-stone-300">p.{(i*3+j+1)*2}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      ),
    ],
  },

  /* ─── 9. SELF CARE JOURNAL — Feminine / Soft ─── */
  'Self Care Journal': {
    genType: 'daily-planner', genTopic: 'self care wellness journal',
    includes: '30+ prompts · Printable · Guided journaling',
    features: ['Gratitude prompts', 'Mood tracker', 'Daily reflections', 'Goal setting', 'Mindfulness exercises', 'Weekly review'],
    faqs: [
      { q: 'How many prompts are included?', a: '30+ unique prompts covering gratitude, self-reflection, goal setting, and mindfulness.' },
      { q: 'Is this suitable for beginners?', a: 'Absolutely! Designed to be accessible for anyone new to journaling.' },
    ],
    reviews: [
      { name: 'Maya J.', rating: 5, text: 'The prompts are so thoughtful. My customers keep messaging about how much they love it.', date: '1 week ago' },
      { name: 'Grace T.', rating: 5, text: 'Beautiful feminine design. Exactly what I needed for my Etsy shop.', date: '3 weeks ago' },
    ],
    pages: [
      ({ accent }) => (
        <div className="w-full h-full flex flex-col items-center justify-center text-center relative overflow-hidden" style={{ background: 'linear-gradient(170deg, #FDF2F7 0%, #F5E8F0 50%, #EDE8F5 100%)' }}>
          <div className="absolute top-[30%] left-0 right-0 h-[1px] opacity-10" style={{ background: `linear-gradient(90deg, transparent, ${accent}, transparent)` }} />
          <div className="absolute top-[60%] left-0 right-0 h-[1px] opacity-10" style={{ background: `linear-gradient(90deg, transparent, ${accent}, transparent)` }} />
          <div className="relative z-10 px-8">
            <p className="text-[9px] tracking-[0.4em] uppercase mb-5 font-sans" style={{ color: accent }}>Inner Peace</p>
            <h2 className="text-[24px] font-serif text-stone-800 leading-[1.2] mb-1">Self Care</h2>
            <h2 className="text-[28px] font-serif font-bold italic leading-[1.1] mb-3" style={{ color: accent }}>Journal</h2>
            <div className="w-12 h-px mx-auto my-4" style={{ background: accent + '30' }} />
            <p className="text-[9px] font-sans text-stone-400 tracking-wide">Nurture your inner world</p>
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#FFFAFD' }}>
          <h3 className="text-xs font-serif font-bold text-stone-700 mb-1">Gratitude Prompts</h3>
          <p className="text-[8px] font-sans text-stone-400 mb-4">Take a moment to reflect...</p>
          <div className="space-y-3.5 flex-1">
            {['What made me smile today?','Someone I\'m grateful for...','A small win I had this week:','Something beautiful I noticed:','What I love about my life:'].map((p,i) => (
              <div key={i}><p className="text-[9px] font-sans font-medium mb-1.5" style={{ color: accent }}>{p}</p><div className="space-y-1"><div className="h-px bg-stone-100 w-full" /><div className="h-px bg-stone-100 w-full" /></div></div>
            ))}
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#FFFAFD' }}>
          <h3 className="text-xs font-serif font-bold text-stone-700 mb-4">Mood Tracker</h3>
          <div className="grid grid-cols-7 gap-1 mb-3">
            {['M','T','W','T','F','S','S'].map((d,i) => (<div key={i} className="text-center text-[7px] font-sans font-bold text-stone-400">{d}</div>))}
            {[...Array(28)].map((_,i) => {
              const moods = ['#F9E3E3','#FCEBD4','#FFF3CD','#D4EDDA','#D1ECF1'];
              return <div key={i} className="aspect-square rounded-full" style={{ background: i<20 ? moods[i%5] : 'transparent', border: `1px solid ${accent}08` }} />;
            })}
          </div>
          <div className="flex justify-center gap-2 mt-1 mb-3">
            {[{l:'Rough',c:'#F9E3E3'},{l:'Meh',c:'#FCEBD4'},{l:'Ok',c:'#FFF3CD'},{l:'Good',c:'#D4EDDA'},{l:'Great',c:'#D1ECF1'}].map(m => (
              <div key={m.l} className="flex items-center gap-0.5"><div className="w-2 h-2 rounded-full" style={{ background: m.c }} /><span className="text-[6px] font-sans text-stone-400">{m.l}</span></div>
            ))}
          </div>
          <div className="mt-auto pt-2 border-t border-stone-100"><p className="text-[8px] font-sans text-stone-400 italic">Every feeling is valid.</p></div>
        </div>
      ),
    ],
  },

  /* ─── 10. MEAL PLANNER — Scandinavian / Clean ─── */
  'Meal Planner': {
    genType: 'daily-planner', genTopic: 'weekly meal planner grocery list',
    includes: '16+ pages · Ultra minimal · Fridge-friendly',
    features: ['Weekly meal grid', 'Grocery list', 'Prep schedule', 'Nutrition notes', 'Favorite meals log', 'Shopping categories'],
    faqs: [
      { q: 'Is this for specific diets?', a: 'No! It is completely flexible. Works for any diet — keto, vegan, paleo, or just balanced eating.' },
      { q: 'Can I print this weekly?', a: 'Yes! Designed to be printed each week and placed on your fridge or kitchen wall.' },
    ],
    reviews: [
      { name: 'Ingrid K.', rating: 5, text: 'Finally a meal planner that looks clean and minimal. Scandinavian perfection.', date: '3 days ago' },
      { name: 'Marie N.', rating: 4, text: 'Simple and functional. Exactly what my customers want.', date: '2 weeks ago' },
    ],
    pages: [
      ({ accent }) => (
        <div className="w-full h-full flex flex-col justify-center relative" style={{ background: '#FFFFFF' }}>
          <div className="px-10">
            <p className="text-[7px] tracking-[0.5em] uppercase mb-8 font-sans text-stone-300">Weekly Plan</p>
            <h2 className="text-[24px] font-sans font-extralight text-stone-400 leading-[1.3]" style={{ fontFamily: 'system-ui' }}>Meal</h2>
            <h2 className="text-[28px] font-sans font-bold text-stone-800 leading-[1.05]" style={{ fontFamily: 'system-ui' }}>Planner</h2>
            <div className="w-8 h-[1px] mt-8" style={{ background: accent }} />
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#FFFFFF' }}>
          <h3 className="text-[10px] font-sans font-bold text-stone-700 mb-3" style={{ fontFamily: 'system-ui' }}>This Week</h3>
          <div className="flex-1 space-y-0">
            {['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'].map((d,i) => (
              <div key={i} className="flex border-b border-stone-50 py-1.5">
                <span className="text-[7px] font-sans text-stone-400 w-14 flex-shrink-0">{d}</span>
                <div className="flex-1 grid grid-cols-3 gap-1">
                  {['B','L','D'].map((meal,j) => (
                    <div key={j} className="text-center">
                      {i===0 && <p className="text-[5px] font-sans text-stone-300 mb-0.5">{['Breakfast','Lunch','Dinner'][j]}</p>}
                      <div className="h-2.5 rounded" style={{ background: (i+j)%3===0 ? accent+'10' : 'transparent' }} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#FFFFFF' }}>
          <h3 className="text-[10px] font-sans font-bold text-stone-700 mb-4" style={{ fontFamily: 'system-ui' }}>Grocery List</h3>
          <div className="grid grid-cols-2 gap-x-4 gap-y-3 flex-1">
            {[{cat:'Produce',items:['Spinach','Tomatoes','Avocados','Lemons']},{cat:'Protein',items:['Chicken breast','Salmon','Eggs','Tofu']},{cat:'Dairy',items:['Greek yogurt','Feta cheese','Milk']},{cat:'Pantry',items:['Olive oil','Rice','Pasta','Garlic']}].map((c,i) => (
              <div key={i}>
                <p className="text-[7px] font-sans font-bold text-stone-400 uppercase tracking-wider mb-1">{c.cat}</p>
                {c.items.map((item,j) => (
                  <div key={j} className="flex items-center gap-1 py-0.5">
                    <div className="w-2 h-2 rounded-sm border" style={{ borderColor: accent+'30' }} />
                    <span className="text-[8px] font-sans text-stone-600">{item}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      ),
    ],
  },

  /* ─── 11. ETSY SHOP PLANNER — Bold / Modern ─── */
  'Etsy Shop Planner': {
    genType: 'ebook', genTopic: 'etsy shop business planner',
    includes: '15+ pages · Business dashboard · SEO guide',
    features: ['Shop dashboard', 'Product tracker', 'SEO keywords', 'Revenue goals', 'Listing calendar', 'Competition analysis'],
    faqs: [
      { q: 'Is this only for Etsy?', a: 'Primarily designed for Etsy sors but many pages work for any online shop — Shopify, Amazon Handmade, etc.' },
      { q: 'Does this include SEO tips?', a: 'Yes! There is a dedicated SEO keyword tracking section and optimization checklist.' },
    ],
    reviews: [
      { name: 'Rachel B.', rating: 5, text: 'Used the SEO section and tripled my views. This planner pays for itself!', date: '5 days ago' },
      { name: 'Kate P.', rating: 4, text: 'Great for new Etsy sors. Covers everything you need to get started.', date: '2 weeks ago' },
    ],
    pages: [
      ({ accent }) => (
        <div className="w-full h-full flex flex-col relative overflow-hidden" style={{ background: '#FFFAF5' }}>
          <div className="h-[45%] flex items-end px-6 pb-4" style={{ background: accent }}>
            <div>
              <p className="text-[8px] tracking-[0.4em] uppercase mb-2 font-sans text-white/60">Etsy Success</p>
              <h2 className="text-[26px] font-sans font-black text-white leading-[1.05]" style={{ fontFamily: 'system-ui' }}>Etsy Shop<br/>Planner</h2>
            </div>
          </div>
          <div className="flex-1 px-6 pt-4 flex items-start">
            <div className="flex gap-4">
              {[{v:'$2.4K',l:'Revenue'},{v:'156',l:'Orders'},{v:'4.8',l:'Rating'}].map((s,i) => (
                <div key={i}>
                  <p className="text-sm font-sans font-black text-stone-800">{s.v}</p>
                  <p className="text-[7px] font-sans text-stone-400">{s.l}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#FFFAF5' }}>
          <h3 className="text-xs font-sans font-bold text-stone-700 mb-4" style={{ fontFamily: 'system-ui' }}>Shop Dashboard</h3>
          <div className="grid grid-cols-2 gap-2 mb-4">
            {[{l:'This Month',v:'$892',d:'+23%',up:true},{l:'Orders',v:'47',d:'+12%',up:true},{l:'Avg. Order',v:'$18.90',d:'-2%',up:false},{l:'Views',v:'3.2K',d:'+45%',up:true}].map((s,i) => (
              <div key={i} className="p-2 rounded-lg bg-white border border-stone-100">
                <p className="text-[7px] font-sans text-stone-400 uppercase">{s.l}</p>
                <div className="flex items-end gap-1">
                  <p className="text-sm font-sans font-black text-stone-800">{s.v}</p>
                  <span className={`text-[7px] font-sans font-bold mb-0.5 ${s.up?'text-emerald-500':'text-red-400'}`}>{s.d}</span>
                </div>
              </div>
            ))}
          </div>
          <p className="text-[8px] font-sans font-bold text-stone-500 uppercase tracking-wider mb-2">Top Products</p>
          <div className="space-y-1.5 flex-1">
            {[{n:'Self Care Planner',s:23,r:'$437'},{n:'Budget Tracker',s:15,r:'$285'},{n:'Recipe Cards',s:9,r:'$170'}].map((p,i) => (
              <div key={i} className="flex items-center justify-between py-1.5 px-2 bg-white rounded-lg border border-stone-100">
                <span className="text-[8px] font-sans text-stone-700">{p.n}</span>
                <div className="flex gap-2">
                  <span className="text-[7px] font-sans text-stone-400">{p.s} sales</span>
                  <span className="text-[7px] font-sans font-bold" style={{ color: accent }}>{p.r}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#FFFAF5' }}>
          <h3 className="text-xs font-sans font-bold text-stone-700 mb-4" style={{ fontFamily: 'system-ui' }}>SEO Keywords</h3>
          <div className="space-y-1.5 flex-1">
            {[{k:'digital planner',v:12400,d:'Low'},{k:'budget template printable',v:8900,d:'Low'},{k:'self care journal PDF',v:6700,d:'Medium'},{k:'wedding planner digital',v:5200,d:'Low'},{k:'meal prep template',v:4800,d:'Medium'},{k:'fitness tracker printable',v:3900,d:'High'}].map((kw,i) => (
              <div key={i} className="flex items-center gap-2 py-1.5 px-2 bg-white rounded-lg border border-stone-100">
                <span className="text-[8px] font-sans text-stone-700 flex-1">{kw.k}</span>
                <span className="text-[7px] font-sans text-stone-400">{kw.v.toLocaleString()}/mo</span>
                <span className={`text-[6px] font-sans font-bold px-1.5 py-0.5 rounded-full ${kw.d==='Low'?'bg-emerald-50 text-emerald-600':kw.d==='Medium'?'bg-amber-50 text-amber-600':'bg-red-50 text-red-600'}`}>{kw.d}</span>
              </div>
            ))}
          </div>
        </div>
      ),
    ],
  },

  /* ─── 12. DIGITAL PRODUCT BUNDLE — Premium / Dark ─── */
  'Digital Product Bundle': {
    genType: 'ebook', genTopic: 'digital product business bundle',
    includes: '50+ pages · 5 products in 1 · Ultimate starter kit',
    features: ['Planner templates', 'Journal layouts', 'Tracker designs', 'Marketing guides', 'Pricing templates', 'Social media kit'],
    faqs: [
      { q: 'What is included in the bundle?', a: 'Five complete products: planner, journal, budget tracker, marketing guide, and social media kit.' },
      { q: 'Can I sell each product separately?', a: 'Yes! Each product has a commercial license. Sell as a bundle or individually.' },
    ],
    reviews: [
      { name: 'Olivia M.', rating: 5, text: 'This bundle launched my entire Etsy shop. 5 products ready to sell instantly!', date: '4 days ago' },
      { name: 'Hannah P.', rating: 5, text: 'Best investment for my digital product business. Outstanding quality.', date: '2 weeks ago' },
    ],
    pages: [
      ({ accent }) => (
        <div className="w-full h-full flex flex-col items-center justify-center text-center relative overflow-hidden" style={{ background: '#1E1B2E' }}>
          <div className="absolute top-[20%] left-1/2 -translate-x-1/2 w-[60%] aspect-square rounded-full opacity-[0.06]" style={{ background: accent }} />
          <div className="absolute bottom-0 left-0 right-0 h-[2px]" style={{ background: `linear-gradient(90deg, transparent, ${accent}60, transparent)` }} />
          <div className="relative z-10 px-6">
            <p className="text-[8px] tracking-[0.5em] uppercase mb-5 font-sans" style={{ color: accent }}>Premium Collection</p>
            <h2 className="text-[22px] font-serif font-bold text-white leading-[1.2] mb-1">Digital Product</h2>
            <h2 className="text-[26px] font-serif italic leading-[1.1] mb-3" style={{ color: accent }}>Bundle</h2>
            <div className="flex items-center justify-center gap-2 my-3">
              <div className="w-6 h-px" style={{ background: accent + '40' }} />
              <div className="w-1.5 h-1.5 rounded-full" style={{ background: accent + '60' }} />
              <div className="w-6 h-px" style={{ background: accent + '40' }} />
            </div>
            <p className="text-[9px] font-sans text-stone-400 tracking-wide">5 products · 1 download</p>
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#1E1B2E' }}>
          <h3 className="text-xs font-serif font-bold text-white mb-4">{"What's Inside"}</h3>
          <div className="space-y-2 flex-1">
            {[{n:'Soft Life Planner',d:'25+ pages',i:'01'},{n:'Self Care Journal',d:'30+ prompts',i:'02'},{n:'Budget Tracker',d:'20+ pages',i:'03'},{n:'Marketing Guide',d:'15+ strategies',i:'04'},{n:'Social Media Kit',d:'50+ templates',i:'05'}].map((item,i) => (
              <div key={i} className="flex items-center gap-3 p-2.5 rounded-xl" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <span className="text-[10px] font-sans font-black" style={{ color: accent }}>{item.i}</span>
                <div className="flex-1"><p className="text-[9px] font-sans font-bold text-stone-200">{item.n}</p><p className="text-[7px] font-sans text-stone-500">{item.d}</p></div>
                <CheckCircle2 className="w-3 h-3 flex-shrink-0" style={{ color: accent }} />
              </div>
            ))}
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#1E1B2E' }}>
          <h3 className="text-xs font-serif font-bold text-white mb-4">Pricing</h3>
          <div className="space-y-2.5 flex-1">
            <div className="p-3 rounded-xl text-center" style={{ background: 'rgba(255,255,255,0.04)' }}>
              <p className="text-[8px] font-sans text-stone-500 uppercase tracking-wider">Individual value</p>
              <p className="text-lg font-serif font-bold line-through text-stone-500 mt-1">$135</p>
            </div>
            <div className="p-4 rounded-xl text-center" style={{ background: accent+'15', border: `2px solid ${accent}30` }}>
              <p className="text-[8px] font-sans font-bold uppercase tracking-wider" style={{ color: accent }}>Bundle price</p>
              <p className="text-2xl font-serif font-bold mt-1 text-white">$47</p>
              <p className="text-[8px] font-sans text-stone-400 mt-1">Save 65%</p>
            </div>
            <div className="space-y-1.5 mt-2">
              {['Instant download','Commercial license','Lifetime updates','Canva editable'].map(f => (
                <div key={f} className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 flex-shrink-0" style={{ color: accent }} /><span className="text-[9px] font-sans text-stone-300">{f}</span></div>
              ))}
            </div>
          </div>
        </div>
      ),
    ],
  },

  /* ─── 13. AI TRAVEL PLANNER — Teal / Tropical ─── */
  'AI Travel Planner': {
    genType: 'daily-planner', genTopic: 'AI travel planner itinerary',
    includes: '35+ pages · Day-by-day itinerary · Budget & packing',
    features: ['Day-by-day itinerary', 'Budget calculator', 'Packing list', 'Restaurant finder', 'Weather overview', 'Emergency contacts'],
    faqs: [
      { q: 'Does the AI create a full itinerary?', a: 'Yes! Enter your destination and dates — AI generates a complete day-by-day plan with activities, dining, and transport.' },
      { q: 'Can I customize the itinerary?', a: 'Absolutely. Every section is editable in Canva. Add, remove, or rearrange days.' },
    ],
    reviews: [
      { name: 'Thomas B.', rating: 5, text: 'Used this for my Italy trip. The day-by-day planner was perfect. Already planning the next trip!', date: '5 days ago' },
      { name: 'Linda H.', rating: 5, text: 'The packing list and budget tracker saved me so much time. Love the tropical design.', date: '2 weeks ago' },
    ],
    pages: [
      ({ accent }) => (
        <div className="w-full h-full flex flex-col items-center justify-center text-center relative overflow-hidden" style={{ background: 'linear-gradient(160deg, #E8F4F0 0%, #D0E8E4 100%)' }}>
          <div className="absolute top-[10%] right-[10%] w-[40%] aspect-square rounded-full opacity-[0.06]" style={{ background: accent }} />
          <div className="absolute bottom-[15%] left-[5%] w-20 h-20 rounded-full opacity-[0.04]" style={{ background: accent }} />
          <div className="relative z-10 px-8">
            <div className="text-[24px] mb-2" style={{ color: accent }}>&#x2708;</div>
            <p className="text-[8px] tracking-[0.5em] uppercase mb-4 font-sans font-bold" style={{ color: accent }}>AI-Powered</p>
            <h2 className="text-[28px] font-sans font-black text-stone-800 leading-[1.05] mb-2" style={{ fontFamily: 'system-ui' }}>Travel<br/>Planner</h2>
            <div className="w-10 h-px mx-auto my-4" style={{ background: accent }} />
            <p className="text-[9px] font-sans text-stone-500 tracking-wide">Your dream trip, planned by AI</p>
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#F0FAF7' }}>
          <h3 className="text-xs font-sans font-bold text-stone-700 mb-1" style={{ fontFamily: 'system-ui' }}>Day-by-Day Itinerary</h3>
          <p className="text-[8px] font-sans text-stone-400 mb-3">Bali, Indonesia · 7 days</p>
          <div className="space-y-2 flex-1">
            {[{d:'Day 1',p:'Ubud Rice Terraces',t:'06:00 - Sunrise trek'},{d:'Day 2',p:'Sacred Monkey Forest',t:'09:00 - Temple visit'},{d:'Day 3',p:'Mount Batur',t:'03:00 - Sunrise hike'},{d:'Day 4',p:'Seminyak Beach',t:'10:00 - Beach day'},{d:'Day 5',p:'Nusa Penida Island',t:'07:00 - Boat trip'},{d:'Day 6',p:'Tanah Lot Temple',t:'15:00 - Sunset'},{d:'Day 7',p:'Departure',t:'10:00 - Airport'}].map((item,i) => (
              <div key={i} className="flex items-center gap-2 py-1 px-2 rounded-lg" style={{ background: i%2===0 ? accent+'08' : 'transparent' }}>
                <span className="text-[7px] font-sans font-bold px-1.5 py-0.5 rounded" style={{ background: accent, color: 'white' }}>{item.d}</span>
                <div className="flex-1"><p className="text-[8px] font-sans font-bold text-stone-700">{item.p}</p><p className="text-[6px] font-sans text-stone-400">{item.t}</p></div>
              </div>
            ))}
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#F0FAF7' }}>
          <h3 className="text-xs font-sans font-bold text-stone-700 mb-3" style={{ fontFamily: 'system-ui' }}>Travel Budget</h3>
          <div className="grid grid-cols-2 gap-2 mb-4">
            {[{l:'Flights',v:'$850',p:35},{l:'Hotels',v:'$620',p:25},{l:'Food',v:'$350',p:14},{l:'Activities',v:'$480',p:20}].map((b,i) => (
              <div key={i} className="p-2 rounded-lg" style={{ background: accent+'08' }}>
                <p className="text-[7px] font-sans text-stone-500">{b.l}</p>
                <p className="text-sm font-sans font-bold" style={{ color: accent }}>{b.v}</p>
                <div className="h-1 rounded-full mt-1" style={{ background: accent+'15' }}><div className="h-full rounded-full" style={{ width: `${b.p*2.5}%`, background: accent }} /></div>
              </div>
            ))}
          </div>
          <div className="mt-auto pt-2 border-t" style={{ borderColor: accent+'15' }}>
            <div className="flex justify-between"><span className="text-[8px] font-sans text-stone-500">Total budget</span><span className="text-[10px] font-sans font-bold" style={{ color: accent }}>$2,450</span></div>
          </div>
        </div>
      ),
    ],
  },

  /* ─── 14. BABY SLEEP TRACKER — Night Blue / Dreamy ─── */
  'Baby Sleep Tracker': {
    genType: 'daily-planner', genTopic: 'baby sleep and feeding log tracker',
    includes: '20+ pages · Night-mode design · Sleep & feed log',
    features: ['Sleep log', 'Feeding tracker', 'Diaper log', 'Growth milestones', 'Nap schedule', 'Night routine'],
    faqs: [
      { q: 'What age is this for?', a: 'Designed for newborns through 12 months. Track sleep patterns, feeding schedules, and daily routines.' },
      { q: 'Can I track multiple babies?', a: 'Yes! Duplicate the pages in Canva for each child. Each section is fully customizable.' },
    ],
    reviews: [
      { name: 'Maria L.', rating: 5, text: 'This saved my sanity as a new mom. Tracking sleep patterns helped me find a routine.', date: '1 week ago' },
      { name: 'Jenny W.', rating: 5, text: 'Beautiful night-theme design. My sleep-deprived brain loves the simple layout.', date: '3 weeks ago' },
    ],
    pages: [
      ({ accent }) => (
        <div className="w-full h-full flex flex-col items-center justify-center text-center relative overflow-hidden" style={{ background: 'linear-gradient(170deg, #E8ECF8 0%, #D8DEF0 50%, #F0ECF8 100%)' }}>
          {[{t:12,l:20,s:4},{t:25,l:70,s:3},{t:60,l:15,s:5},{t:45,l:80,s:3},{t:80,l:50,s:4}].map((d,i) => (
            <div key={i} className="absolute rounded-full" style={{ top:`${d.t}%`,left:`${d.l}%`,width:`${d.s*4}px`,height:`${d.s*4}px`,background: accent+'15' }} />
          ))}
          <div className="relative z-10 px-6">
            <div className="text-[22px] mb-3" style={{ color: accent }}>&#x263E;</div>
            <p className="text-[8px] tracking-[0.4em] uppercase mb-4 font-sans" style={{ color: accent }}>Sweet Dreams</p>
            <h2 className="text-[24px] font-sans font-extrabold text-stone-700 leading-[1.15] mb-2" style={{ fontFamily: 'system-ui' }}>Baby Sleep</h2>
            <h2 className="text-[22px] font-sans font-extrabold leading-[1.1] mb-3" style={{ color: accent, fontFamily: 'system-ui' }}>Tracker</h2>
            <p className="text-[9px] font-sans text-stone-400">Sleep · Feed · Grow</p>
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#F5F5FC' }}>
          <h3 className="text-xs font-sans font-extrabold text-stone-700 mb-3" style={{ fontFamily: 'system-ui' }}>Sleep Log</h3>
          <div className="space-y-1.5 flex-1">
            {[{t:'8:00 PM',e:'11:30 PM',d:'3.5 hrs',ty:'Night'},{t:'12:00 AM',e:'3:00 AM',d:'3 hrs',ty:'Night'},{t:'3:30 AM',e:'6:00 AM',d:'2.5 hrs',ty:'Night'},{t:'9:00 AM',e:'10:30 AM',d:'1.5 hrs',ty:'Nap'},{t:'1:00 PM',e:'3:00 PM',d:'2 hrs',ty:'Nap'},{t:'5:00 PM',e:'5:45 PM',d:'45 min',ty:'Nap'}].map((s,i) => (
              <div key={i} className="flex items-center justify-between py-1.5 px-2 rounded-lg" style={{ background: s.ty==='Night' ? accent+'08' : 'transparent' }}>
                <div className="flex items-center gap-2">
                  <span className="text-[8px]">{s.ty==='Night' ? '🌙' : '☀️'}</span>
                  <span className="text-[8px] font-sans text-stone-600">{s.t} – {s.e}</span>
                </div>
                <span className="text-[8px] font-sans font-bold" style={{ color: accent }}>{s.d}</span>
              </div>
            ))}
          </div>
          <div className="mt-2 pt-2 border-t" style={{ borderColor: accent+'15' }}>
            <div className="flex justify-between"><span className="text-[8px] font-sans text-stone-400">Total sleep</span><span className="text-[9px] font-sans font-bold" style={{ color: accent }}>13.25 hrs</span></div>
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#F5F5FC' }}>
          <h3 className="text-xs font-sans font-extrabold text-stone-700 mb-3" style={{ fontFamily: 'system-ui' }}>Feeding Log</h3>
          <div className="grid grid-cols-3 gap-2 mb-3">
            {[{l:'Breast',v:'6x',c:accent},{l:'Bottle',v:'2x',c:'#E8A0BF'},{l:'Solids',v:'1x',c:'#8FAE80'}].map((f,i) => (
              <div key={i} className="p-2 rounded-lg text-center" style={{ background: f.c+'10' }}>
                <p className="text-[7px] font-sans text-stone-500">{f.l}</p>
                <p className="text-sm font-sans font-bold" style={{ color: f.c }}>{f.v}</p>
              </div>
            ))}
          </div>
          <div className="space-y-1.5 flex-1">
            {[{t:'6:30 AM',ty:'Breast',d:'15 min'},{t:'9:00 AM',ty:'Bottle',d:'120 ml'},{t:'11:30 AM',ty:'Breast',d:'20 min'},{t:'2:00 PM',ty:'Solids',d:'Banana + oats'},{t:'5:00 PM',ty:'Breast',d:'15 min'},{t:'7:30 PM',ty:'Bottle',d:'150 ml'}].map((f,i) => (
              <div key={i} className="flex items-center justify-between py-1 border-b border-stone-50">
                <span className="text-[8px] font-sans text-stone-600">{f.t} — {f.ty}</span>
                <span className="text-[8px] font-sans text-stone-400">{f.d}</span>
              </div>
            ))}
          </div>
        </div>
      ),
    ],
  },

  /* ─── 15. PUPPY JOURNAL — Playful / Warm Amber ─── */
  'Puppy Journal': {
    genType: 'daily-planner', genTopic: 'puppy training journal pet tracker',
    includes: '22+ pages · Playful design · Training & health',
    features: ['Training log', 'Vet visit tracker', 'Vaccine record', 'Walking schedule', 'Growth chart', 'Feeding plan'],
    faqs: [
      { q: 'Is this for puppies only?', a: 'Works for dogs of all ages! The training log and vet tracker are useful throughout your dog\'s life.' },
      { q: 'Can I add photos of my pet?', a: 'Yes! Each section has photo placeholders. Add pictures in Canva for a personalized keepsake.' },
    ],
    reviews: [
      { name: 'Sarah D.', rating: 5, text: 'Got this when we brought our golden retriever home. The vaccine tracker is so helpful!', date: '4 days ago' },
      { name: 'Mike R.', rating: 5, text: 'Cute design and very practical. My wife uses it daily for our new puppy.', date: '2 weeks ago' },
    ],
    pages: [
      ({ accent }) => (
        <div className="w-full h-full flex flex-col items-center justify-center text-center relative overflow-hidden" style={{ background: 'linear-gradient(155deg, #FFF8E1 0%, #FFEFC8 100%)' }}>
          <div className="absolute top-[15%] left-[15%] w-8 h-8 rounded-full opacity-10" style={{ background: accent }} />
          <div className="absolute bottom-[20%] right-[10%] w-12 h-12 rounded-full opacity-10" style={{ background: accent }} />
          <div className="relative z-10 px-6">
            <div className="text-[26px] mb-3">&#x1F43E;</div>
            <p className="text-[8px] tracking-[0.4em] uppercase mb-4 font-sans font-bold" style={{ color: accent }}>Best Friend</p>
            <h2 className="text-[26px] font-sans font-black text-stone-800 leading-[1.1] mb-2" style={{ fontFamily: 'system-ui' }}>Puppy</h2>
            <h2 className="text-[24px] font-sans font-black leading-[1.1] mb-3" style={{ color: accent, fontFamily: 'system-ui' }}>Journal</h2>
            <p className="text-[9px] font-sans text-stone-500">Every wag, every milestone</p>
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#FFFCF0' }}>
          <h3 className="text-xs font-sans font-bold text-stone-700 mb-3" style={{ fontFamily: 'system-ui' }}>Training Log</h3>
          <div className="space-y-1.5 flex-1">
            {[{cmd:'Sit',s:'Mastered',p:100},{cmd:'Stay',s:'Learning',p:60},{cmd:'Come',s:'Learning',p:45},{cmd:'Down',s:'Started',p:25},{cmd:'Paw',s:'Started',p:20},{cmd:'Leave it',s:'Not started',p:0}].map((c,i) => (
              <div key={i} className="py-1.5">
                <div className="flex justify-between mb-0.5"><span className="text-[8px] font-sans font-bold text-stone-700">{c.cmd}</span><span className="text-[7px] font-sans" style={{ color: c.p>50?'#4CAF50':accent }}>{c.s}</span></div>
                <div className="h-1.5 rounded-full" style={{ background: accent+'12' }}><div className="h-full rounded-full" style={{ width:`${c.p}%`, background: c.p>50?'#4CAF50':accent }} /></div>
              </div>
            ))}
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#FFFCF0' }}>
          <h3 className="text-xs font-sans font-bold text-stone-700 mb-3" style={{ fontFamily: 'system-ui' }}>Vet & Vaccines</h3>
          <div className="space-y-2 flex-1">
            {[{v:'DHPP',d:'Jan 15',n:'Done'},{v:'Rabies',d:'Feb 20',n:'Done'},{v:'Bordetella',d:'Mar 10',n:'Done'},{v:'Lyme',d:'Apr 5',n:'Scheduled'},{v:'Deworming',d:'Monthly',n:'Ongoing'}].map((v,i) => (
              <div key={i} className="flex items-center justify-between py-1.5 px-2 rounded-lg" style={{ background: v.n==='Done'?'#E8F5E9':accent+'08' }}>
                <div><p className="text-[8px] font-sans font-bold text-stone-700">{v.v}</p><p className="text-[7px] font-sans text-stone-400">{v.d}</p></div>
                <span className={`text-[7px] font-sans font-bold px-2 py-0.5 rounded-full ${v.n==='Done'?'bg-emerald-100 text-emerald-600':'bg-amber-50 text-amber-600'}`}>{v.n}</span>
              </div>
            ))}
          </div>
        </div>
      ),
    ],
  },

  /* ─── 16. 12-WEEK WORKOUT — Neon Green / Athletic ─── */
  '12-Week Workout': {
    genType: 'daily-planner', genTopic: '12 week workout training program',
    includes: '24+ pages · Split program · Progress tracker',
    features: ['12-week program', 'Weekly split', 'Exercise library', 'Progress photos', 'Nutrition guide', 'Rest day planner'],
    faqs: [
      { q: 'What fitness level is this for?', a: 'Suitable for beginners to intermediate. Each exercise has variations for different levels.' },
      { q: 'Does it include a meal plan?', a: 'Yes! Includes a basic nutrition guide with macro targets and sample meal plans.' },
    ],
    reviews: [
      { name: 'Chris P.', rating: 5, text: 'Lost 8kg in 12 weeks following this program. The progressive overload is perfectly planned.', date: '1 week ago' },
      { name: 'Dave K.', rating: 5, text: 'The neon design is fire. My gym buddies all want a copy. Great Etsy sor.', date: '3 weeks ago' },
    ],
    pages: [
      ({ accent }) => (
        <div className="w-full h-full flex flex-col items-center justify-center text-center relative overflow-hidden" style={{ background: '#0D0D0D' }}>
          <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: `linear-gradient(90deg, transparent, ${accent}, transparent)` }} />
          <div className="absolute bottom-0 left-0 right-0 h-[2px]" style={{ background: `linear-gradient(90deg, transparent, ${accent}, transparent)` }} />
          <div className="absolute top-[20%] right-[10%] w-[30%] aspect-square rounded-full opacity-[0.05]" style={{ background: accent }} />
          <div className="relative z-10 px-6">
            <p className="text-[9px] tracking-[0.5em] uppercase mb-4 font-sans font-bold" style={{ color: accent }}>Transform</p>
            <h2 className="text-[28px] font-sans font-black text-white leading-[1.05] mb-1" style={{ fontFamily: 'system-ui' }}>12-Week</h2>
            <h2 className="text-[30px] font-sans font-black leading-[1.05] mb-3" style={{ color: accent, fontFamily: 'system-ui' }}>Workout</h2>
            <p className="text-[9px] font-sans text-stone-500">Build muscle. Burn fat. Get results.</p>
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#0D0D0D' }}>
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-xs font-sans font-bold text-white" style={{ fontFamily: 'system-ui' }}>Week 1 · Push Day</h3>
            <span className="text-[7px] font-sans px-2 py-0.5 rounded-full" style={{ background: accent+'20', color: accent }}>Phase 1</span>
          </div>
          <div className="space-y-1.5 flex-1">
            {[{ex:'Flat Bench Press',s:'4×10',r:'60s'},{ex:'Incline DB Press',s:'3×12',r:'60s'},{ex:'Cable Flyes',s:'3×15',r:'45s'},{ex:'OHP',s:'4×8',r:'90s'},{ex:'Lateral Raises',s:'4×12',r:'45s'},{ex:'Tricep Pushdown',s:'3×15',r:'45s'}].map((e,i) => (
              <div key={i} className="flex items-center justify-between py-1.5 px-2 rounded-lg" style={{ background: '#1A1A1A' }}>
                <span className="text-[8px] font-sans text-stone-300">{e.ex}</span>
                <div className="flex gap-3"><span className="text-[7px] font-sans" style={{ color: accent }}>{e.s}</span><span className="text-[7px] font-sans text-stone-500">{e.r}</span></div>
              </div>
            ))}
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#0D0D0D' }}>
          <h3 className="text-xs font-sans font-bold text-white mb-3" style={{ fontFamily: 'system-ui' }}>12-Week Progress</h3>
          <div className="grid grid-cols-3 gap-2 mb-4">
            {[{l:'Week',v:'1/12'},{l:'Sessions',v:'3/4'},{l:'Streak',v:'3'}].map((s,i) => (
              <div key={i} className="p-2 rounded-lg text-center" style={{ background: '#1A1A1A' }}>
                <p className="text-[7px] font-sans text-stone-500">{s.l}</p>
                <p className="text-sm font-sans font-black" style={{ color: accent }}>{s.v}</p>
              </div>
            ))}
          </div>
          <div className="space-y-2 flex-1">
            {[{w:'Week 1',c:1},{w:'Week 2',c:0.9},{w:'Week 3',c:0.7},{w:'Week 4',c:0.5},{w:'Week 5–12',c:0}].map((w,i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-[7px] font-sans text-stone-500 w-16">{w.w}</span>
                <div className="flex-1 h-2 rounded-full" style={{ background: '#1A1A1A' }}><div className="h-full rounded-full" style={{ width:`${w.c*100}%`, background: w.c>0? accent:'transparent' }} /></div>
              </div>
            ))}
          </div>
        </div>
      ),
    ],
  },

  /* ─── 17. SAVINGS GOAL TRACKER — Geometric Blue ─── */
  'Savings Goal Tracker': {
    genType: 'daily-planner', genTopic: 'savings goal house wedding vacation tracker',
    includes: '18+ pages · Goal-focused · Visual progress',
    features: ['Savings goals', 'Monthly tracker', 'Visual progress', 'Milestone rewards', 'Emergency fund', 'Subscription audit'],
    faqs: [
      { q: 'Can I track multiple goals?', a: 'Yes! Set up to 5 simultaneous savings goals with individual progress tracking.' },
      { q: 'Does it work with any currency?', a: 'Absolutely. All amounts are placeholders you can customize to your currency.' },
    ],
    reviews: [
      { name: 'Karen S.', rating: 5, text: 'Saved $15,000 for our house deposit using this tracker. The visual progress is so motivating!', date: '1 week ago' },
      { name: 'Peter N.', rating: 5, text: 'Clean design and functional. My Etsy customers love the geometric style.', date: '3 weeks ago' },
    ],
    pages: [
      ({ accent }) => (
        <div className="w-full h-full flex flex-col items-center justify-center text-center relative overflow-hidden" style={{ background: '#F0F5FF' }}>
          <div className="absolute top-[20%] left-[10%] w-16 h-16 border-2 rotate-45 opacity-10" style={{ borderColor: accent }} />
          <div className="absolute bottom-[15%] right-[15%] w-10 h-10 border-2 rotate-12 opacity-10" style={{ borderColor: accent }} />
          <div className="absolute top-[50%] right-[5%] w-6 h-6 opacity-10 rounded-full" style={{ background: accent }} />
          <div className="relative z-10 px-8">
            <p className="text-[8px] tracking-[0.5em] uppercase mb-5 font-sans font-bold" style={{ color: accent }}>Financial Freedom</p>
            <h2 className="text-[26px] font-sans font-black text-stone-800 leading-[1.1] mb-2" style={{ fontFamily: 'system-ui' }}>Savings Goal</h2>
            <h2 className="text-[22px] font-sans font-black leading-[1.1] mb-3" style={{ color: accent, fontFamily: 'system-ui' }}>Tracker</h2>
            <div className="flex gap-1 justify-center mt-4">{[...Array(5)].map((_,i) => <div key={i} className="w-2 h-2 rounded-sm" style={{ background: accent, opacity: 1-i*0.15 }} />)}</div>
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#F8FAFF' }}>
          <h3 className="text-xs font-sans font-bold text-stone-700 mb-3" style={{ fontFamily: 'system-ui' }}>My Goals</h3>
          <div className="space-y-2.5 flex-1">
            {[{g:'House deposit',t:'$50,000',s:'$32,000',p:64},{g:'Wedding fund',t:'$15,000',s:'$8,500',p:57},{g:'Emergency fund',t:'$10,000',s:'$10,000',p:100},{g:'Vacation',t:'$3,000',s:'$1,200',p:40}].map((g,i) => (
              <div key={i} className="p-2.5 rounded-xl" style={{ background: 'white', border: '1px solid #E8EDF5' }}>
                <div className="flex justify-between mb-1"><span className="text-[8px] font-sans font-bold text-stone-700">{g.g}</span><span className="text-[7px] font-sans" style={{ color: accent }}>{g.s} / {g.t}</span></div>
                <div className="h-2 rounded-full" style={{ background: accent+'12' }}><div className="h-full rounded-full transition-all" style={{ width:`${g.p}%`, background: g.p>=100?'#4CAF50':accent }} /></div>
              </div>
            ))}
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#F8FAFF' }}>
          <h3 className="text-xs font-sans font-bold text-stone-700 mb-3" style={{ fontFamily: 'system-ui' }}>Monthly Savings</h3>
          <div className="space-y-1 flex-1">
            {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m,i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-[7px] font-sans text-stone-400 w-6">{m}</span>
                <div className="flex-1 h-3 rounded" style={{ background: accent+'08' }}><div className="h-full rounded" style={{ width: i<6?`${40+i*10}%`:'0%', background: accent }} /></div>
                {i<6 && <span className="text-[7px] font-sans font-bold" style={{ color: accent }}>${(800+i*200)}</span>}
              </div>
            ))}
          </div>
        </div>
      ),
    ],
  },

  /* ─── 18. HOME MAINTENANCE LOG — Blueprint / Technical ─── */
  'Home Maintenance Log': {
    genType: 'daily-planner', genTopic: 'home maintenance seasonal checklist planner',
    includes: '20+ pages · Seasonal tasks · Room-by-room',
    features: ['Seasonal checklist', 'Room-by-room guide', 'Contractor contacts', 'Budget tracker', 'Warranty log', 'Appliance schedule'],
    faqs: [
      { q: 'Is this for homeowners only?', a: 'Great for both homeowners and renters. Track maintenance, warranties, and important contacts.' },
      { q: 'How often should I update it?', a: 'Review quarterly. The seasonal checklists guide you through what to check each season.' },
    ],
    reviews: [
      { name: 'Robert J.', rating: 5, text: 'Finally organized all our home maintenance. The seasonal checklist is a game changer.', date: '6 days ago' },
      { name: 'Anne M.', rating: 4, text: 'Great for first-time homeowners. Covers everything I didn\'t know I needed to track.', date: '1 month ago' },
    ],
    pages: [
      ({ accent }) => (
        <div className="w-full h-full flex flex-col justify-center relative overflow-hidden" style={{ background: '#F5F5F0' }}>
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: `repeating-linear-gradient(0deg, ${accent} 0px, ${accent} 1px, transparent 1px, transparent 40px), repeating-linear-gradient(90deg, ${accent} 0px, ${accent} 1px, transparent 1px, transparent 40px)` }} />
          <div className="absolute left-0 top-0 bottom-0 w-[5px]" style={{ background: accent }} />
          <div className="pl-8 pr-6 relative z-10">
            <p className="text-[8px] tracking-[0.4em] uppercase mb-4 font-sans font-bold" style={{ color: accent }}>Homeowner</p>
            <h2 className="text-[26px] font-sans font-black text-stone-800 leading-[1.05] mb-2" style={{ fontFamily: 'system-ui' }}>Home<br/>Maintenance</h2>
            <div className="w-8 h-[2px] my-4" style={{ background: accent }} />
            <p className="text-[9px] font-sans text-stone-500">Keep your home running smoothly</p>
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#FAFAF5' }}>
          <h3 className="text-xs font-sans font-bold text-stone-700 mb-3" style={{ fontFamily: 'system-ui' }}>Spring Checklist</h3>
          <div className="space-y-1.5 flex-1">
            {[{t:'Clean gutters',d:'Remove debris from winter storms',done:true},{t:'HVAC service',d:'Schedule annual AC maintenance',done:true},{t:'Check roof',d:'Inspect for missing shingles',done:false},{t:'Power wash deck',d:'Remove mold and stains',done:false},{t:'Test smoke detectors',d:'Replace batteries',done:true},{t:'Lawn care',d:'First mow and fertilize',done:false},{t:'Window seals',d:'Check for drafts and cracks',done:false}].map((c,i) => (
              <div key={i} className="flex items-center gap-2 py-1">
                <div className="w-3 h-3 rounded border-[1.5px] flex-shrink-0 flex items-center justify-center" style={{ borderColor: accent+'40', background: c.done ? accent+'15' : 'transparent' }}>
                  {c.done && <CheckCircle2 className="w-2.5 h-2.5" style={{ color: accent }} />}
                </div>
                <div><p className={`text-[8px] font-sans font-bold ${c.done?'text-stone-400 line-through':'text-stone-700'}`}>{c.t}</p><p className="text-[6px] font-sans text-stone-400">{c.d}</p></div>
              </div>
            ))}
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#FAFAF5' }}>
          <h3 className="text-xs font-sans font-bold text-stone-700 mb-3" style={{ fontFamily: 'system-ui' }}>Room Tracker</h3>
          <div className="space-y-2 flex-1">
            {[{r:'Kitchen',tasks:3,done:2},{r:'Bathroom',tasks:4,done:1},{r:'Living Room',tasks:2,done:2},{r:'Bedroom',tasks:2,done:0},{r:'Garage',tasks:5,done:3},{r:'Garden',tasks:4,done:1}].map((r,i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-[8px] font-sans text-stone-600 w-16">{r.r}</span>
                <div className="flex-1 h-2 rounded-full" style={{ background: accent+'10' }}><div className="h-full rounded-full" style={{ width:`${(r.done/r.tasks)*100}%`, background: r.done===r.tasks?'#4CAF50':accent }} /></div>
                <span className="text-[7px] font-sans text-stone-400">{r.done}/{r.tasks}</span>
              </div>
            ))}
          </div>
        </div>
      ),
    ],
  },

  /* ─── 19. WEDDING GUEST BOOK — Romantic Blush ─── */
  'Wedding Guest Book': {
    genType: 'daily-planner', genTopic: 'wedding guest book messages keepsake',
    includes: '40+ pages · Romantic design · Guest messages',
    features: ['Guest message pages', 'Photo memories', 'Advice cards', 'Well wishes', 'Table assignments', 'RSVP tracker'],
    faqs: [
      { q: 'How many guests can this hold?', a: 'Includes 40+ message pages. Each page fits 2-3 guest entries. Duplicate in Canva for larger weddings.' },
      { q: 'Can I print this as a real book?', a: 'Yes! Optimized for print services like Blurb and Shutterfly.' },
    ],
    reviews: [
      { name: 'Victoria S.', rating: 5, text: 'The most beautiful guest book design. Our guests loved writing their messages in it.', date: '1 week ago' },
      { name: 'Emma L.', rating: 5, text: 'We printed this as a hardcover book after the wedding. A treasured keepsake!', date: '1 month ago' },
    ],
    pages: [
      ({ accent }) => (
        <div className="w-full h-full flex flex-col items-center justify-center text-center relative overflow-hidden" style={{ background: '#FFF5F5' }}>
          <div className="absolute inset-6 border border-dashed rounded-xl opacity-15" style={{ borderColor: accent }} />
          <div className="absolute top-[8%] left-1/2 -translate-x-1/2 w-[50%] h-px opacity-20" style={{ background: accent }} />
          <div className="absolute bottom-[8%] left-1/2 -translate-x-1/2 w-[50%] h-px opacity-20" style={{ background: accent }} />
          <div className="relative z-10 px-6">
            <p className="text-[8px] tracking-[0.5em] uppercase mb-5 font-serif italic" style={{ color: accent }}>With Love</p>
            <h2 className="text-[22px] font-serif italic text-stone-700 leading-[1.3]">Our</h2>
            <h2 className="text-[28px] font-serif font-bold text-stone-800 leading-[1.1] mb-2">Guest Book</h2>
            <div className="flex items-center justify-center gap-2 my-3"><div className="w-5 h-px" style={{ background: accent+'50' }} /><div className="text-[14px]" style={{ color: accent }}>&#x2661;</div><div className="w-5 h-px" style={{ background: accent+'50' }} /></div>
            <p className="text-[9px] font-serif italic text-stone-400">Messages from our loved ones</p>
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#FFFAFA' }}>
          <h3 className="text-xs font-serif font-bold text-stone-700 mb-4">A Message for the Couple</h3>
          <div className="space-y-4 flex-1">
            <div className="p-3 rounded-xl" style={{ background: accent+'06', border: `1px dashed ${accent}20` }}>
              <p className="text-[9px] font-serif italic text-stone-500 leading-relaxed mb-2">{'"May your love grow stronger with every passing year. Wishing you endless happiness and laughter together."'}</p>
              <p className="text-[8px] font-sans font-bold text-stone-600 text-right">— Sarah & James</p>
            </div>
            <div className="p-3 rounded-xl" style={{ background: accent+'06', border: `1px dashed ${accent}20` }}>
              <p className="text-[9px] font-serif italic text-stone-500 leading-relaxed mb-2">{'"So happy to celebrate this beautiful day with you both. Here\'s to a lifetime of adventure!"'}</p>
              <p className="text-[8px] font-sans font-bold text-stone-600 text-right">— Uncle Tom</p>
            </div>
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col items-center justify-center" style={{ background: '#FFFAFA' }}>
          <p className="text-[8px] font-serif italic text-stone-400 mb-4 tracking-wider">Photo Memory</p>
          <div className="w-[70%] aspect-square rounded-2xl flex items-center justify-center mb-4" style={{ background: accent+'06', border: `2px dashed ${accent}20` }}>
            <p className="text-[10px] font-serif italic text-stone-300">Place photo here</p>
          </div>
          <div className="w-[70%] space-y-2"><div className="h-px" style={{ background: accent+'15' }} /><p className="text-[8px] font-serif italic text-stone-300 text-center">Write your caption</p></div>
        </div>
      ),
    ],
  },

  /* ─── 20. STUDY PLANNER — Academic Brown / Classic ─── */
  'Study Planner': {
    genType: 'daily-planner', genTopic: 'student study planner exam schedule',
    includes: '22+ pages · Academic design · Semester planner',
    features: ['Semester overview', 'Exam schedule', 'Assignment tracker', 'Grade calculator', 'Study timetable', 'Reading log'],
    faqs: [
      { q: 'Which academic level is this for?', a: 'Works for high school, university, and graduate students. Fully customizable to your schedule.' },
      { q: 'Can I track multiple subjects?', a: 'Yes! Includes sections for up to 8 subjects with individual grade tracking.' },
    ],
    reviews: [
      { name: 'Alex T.', rating: 5, text: 'Got straight A\'s last semester using this planner. The exam countdown is so helpful.', date: '3 days ago' },
      { name: 'Jessica P.', rating: 5, text: 'Love the classic academic feel. Much better than generic planners.', date: '2 weeks ago' },
    ],
    pages: [
      ({ accent }) => (
        <div className="w-full h-full flex flex-col items-center justify-center text-center relative" style={{ background: '#F5F0E8' }}>
          <div className="absolute inset-5 border-2 rounded-sm" style={{ borderColor: accent+'20' }} />
          <div className="relative z-10 px-8">
            <p className="text-[8px] tracking-[0.4em] uppercase mb-5 font-sans" style={{ color: accent }}>Academic Planner</p>
            <h2 className="text-[28px] font-serif font-bold text-stone-800 leading-[1.1] mb-2">Study</h2>
            <h2 className="text-[26px] font-serif italic text-stone-700 leading-[1.1] mb-3">Planner</h2>
            <div className="w-12 h-px mx-auto my-4" style={{ background: accent }} />
            <p className="text-[9px] font-serif italic text-stone-400">Plan. Study. Succeed.</p>
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#FAF7F0' }}>
          <h3 className="text-xs font-serif font-bold text-stone-700 mb-3">Semester Overview</h3>
          <div className="space-y-1.5 flex-1">
            {[{s:'Mathematics',g:'A',c:'92%'},{s:'Physics',g:'B+',c:'87%'},{s:'Chemistry',g:'A-',c:'90%'},{s:'English',g:'A',c:'95%'},{s:'History',g:'B',c:'83%'},{s:'Programming',g:'A+',c:'98%'}].map((s,i) => (
              <div key={i} className="flex items-center justify-between py-1.5 px-2 rounded-lg" style={{ background: i%2===0 ? accent+'06' : 'transparent' }}>
                <span className="text-[8px] font-sans text-stone-700">{s.s}</span>
                <div className="flex items-center gap-2">
                  <span className="text-[8px] font-sans text-stone-400">{s.c}</span>
                  <span className="text-[8px] font-sans font-bold px-1.5 py-0.5 rounded" style={{ background: accent+'15', color: accent }}>{s.g}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#FAF7F0' }}>
          <h3 className="text-xs font-serif font-bold text-stone-700 mb-3">Exam Schedule</h3>
          <div className="space-y-2 flex-1">
            {[{s:'Mathematics',d:'May 15',t:'09:00',days:12},{s:'Physics',d:'May 18',t:'13:00',days:15},{s:'Chemistry',d:'May 22',t:'09:00',days:19},{s:'English',d:'May 25',t:'10:00',days:22}].map((e,i) => (
              <div key={i} className="p-2.5 rounded-lg" style={{ background: 'white', border: '1px solid #E8E0D5' }}>
                <div className="flex justify-between items-center">
                  <div><p className="text-[9px] font-sans font-bold text-stone-700">{e.s}</p><p className="text-[7px] font-sans text-stone-400">{e.d} · {e.t}</p></div>
                  <div className="text-center"><p className="text-sm font-sans font-black" style={{ color: accent }}>{e.days}</p><p className="text-[6px] font-sans text-stone-400">days</p></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ),
    ],
  },

  /* ─── 21. PINTEREST STRATEGY — Pin Red / Social ─── */
  'Pinterest Strategy Guide': {
    genType: 'ebook', genTopic: 'pinterest marketing strategy for sors',
    includes: '18+ pages · Growth strategy · Pin templates',
    features: ['Pin design guide', 'Board strategy', 'SEO keywords', 'Analytics tracker', 'Content calendar', 'Audience growth plan'],
    faqs: [
      { q: 'Is this updated for the latest algorithm?', a: 'Yes! Includes the latest Pinterest algorithm changes and best practices for maximum reach.' },
      { q: 'Do I need a business account?', a: 'A Pinterest business account is recommended. The guide includes setup instructions.' },
    ],
    reviews: [
      { name: 'Clara B.', rating: 5, text: 'Went from 100 to 10,000 monthly views in 3 months following this strategy.', date: '5 days ago' },
      { name: 'Diana R.', rating: 5, text: 'The SEO keyword section alone is worth it. My pins are finally getting seen!', date: '3 weeks ago' },
    ],
    pages: [
      ({ accent }) => (
        <div className="w-full h-full flex flex-col items-center justify-center text-center relative overflow-hidden" style={{ background: '#FFF0F3' }}>
          <div className="absolute top-[10%] left-[10%] w-12 h-16 rounded-xl opacity-10 rotate-[-8deg]" style={{ background: accent }} />
          <div className="absolute top-[8%] left-[30%] w-10 h-14 rounded-xl opacity-8 rotate-[5deg]" style={{ background: accent+'80' }} />
          <div className="absolute bottom-[15%] right-[10%] w-11 h-15 rounded-xl opacity-8 rotate-[12deg]" style={{ background: accent+'60' }} />
          <div className="relative z-10 px-6">
            <p className="text-[8px] tracking-[0.5em] uppercase mb-4 font-sans font-bold" style={{ color: accent }}>Grow Your Brand</p>
            <h2 className="text-[24px] font-sans font-black text-stone-800 leading-[1.1] mb-1" style={{ fontFamily: 'system-ui' }}>Pinterest</h2>
            <h2 className="text-[22px] font-sans font-black leading-[1.1] mb-3" style={{ color: accent, fontFamily: 'system-ui' }}>Strategy</h2>
            <p className="text-[9px] font-sans text-stone-500">From 0 to 100K monthly views</p>
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#FFF8F9' }}>
          <h3 className="text-xs font-sans font-bold text-stone-700 mb-3" style={{ fontFamily: 'system-ui' }}>Growth Dashboard</h3>
          <div className="grid grid-cols-2 gap-2 mb-3">
            {[{l:'Monthly Views',v:'45.2K',d:'+312%'},{l:'Followers',v:'2,840',d:'+156%'},{l:'Saves',v:'1,230',d:'+89%'},{l:'Link Clicks',v:'567',d:'+204%'}].map((s,i) => (
              <div key={i} className="p-2 rounded-lg bg-white border border-stone-100">
                <p className="text-[7px] font-sans text-stone-400">{s.l}</p>
                <p className="text-sm font-sans font-black text-stone-800">{s.v}</p>
                <span className="text-[7px] font-sans text-emerald-500 font-bold">{s.d}</span>
              </div>
            ))}
          </div>
          <p className="text-[8px] font-sans font-bold text-stone-500 uppercase tracking-wider mb-2">Top Pins</p>
          <div className="space-y-1 flex-1">
            {[{t:'10 Self-Care Tips',v:'12K views'},{t:'Budget Template Free',v:'8.5K views'},{t:'Morning Routine Guide',v:'6.2K views'}].map((p,i) => (
              <div key={i} className="flex items-center justify-between py-1.5 px-2 bg-white rounded-lg border border-stone-100">
                <span className="text-[8px] font-sans text-stone-700">{p.t}</span>
                <span className="text-[7px] font-sans font-bold" style={{ color: accent }}>{p.v}</span>
              </div>
            ))}
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#FFF8F9' }}>
          <h3 className="text-xs font-sans font-bold text-stone-700 mb-3" style={{ fontFamily: 'system-ui' }}>Pin Calendar</h3>
          <div className="grid grid-cols-7 gap-0.5 flex-1">
            {['M','T','W','T','F','S','S'].map((d,i) => (<div key={i} className="text-center text-[6px] font-sans font-bold text-stone-400">{d}</div>))}
            {[...Array(28)].map((_,i) => (
              <div key={i} className="aspect-square rounded flex items-center justify-center" style={{ background: i%3===0 ? accent+'15' : i%5===0 ? '#E8F5E9' : 'white', border: '1px solid #f0f0f0' }}>
                <span className="text-[5px] font-sans text-stone-400">{i+1}</span>
              </div>
            ))}
          </div>
        </div>
      ),
    ],
  },

  /* ─── 22. FAMILY WEEKLY ORGANIZER — Warm Orange ─── */
  'Family Weekly Organizer': {
    genType: 'daily-planner', genTopic: 'family weekly organizer schedule planner',
    includes: '20+ pages · Family-friendly · Weekly grid',
    features: ['Weekly schedule', 'Chore chart', 'Meal plan', 'Activity tracker', 'Carpool planner', 'Family calendar'],
    faqs: [
      { q: 'How many family members can this handle?', a: 'Designed for families of up to 6. Each member gets their own color-coded row in the weekly grid.' },
      { q: 'Can kids use this too?', a: 'Yes! The chore chart and activity sections are designed to be kid-friendly and motivating.' },
    ],
    reviews: [
      { name: 'Christina M.', rating: 5, text: 'Our family finally has a system. No more "who\'s picking up who?" chaos!', date: '4 days ago' },
      { name: 'Henrik O.', rating: 5, text: 'The chore chart motivated my kids to actually help around the house. Genius!', date: '2 weeks ago' },
    ],
    pages: [
      ({ accent }) => (
        <div className="w-full h-full flex flex-col items-center justify-center text-center relative overflow-hidden" style={{ background: 'linear-gradient(160deg, #FFF5E5 0%, #FFE8CC 100%)' }}>
          <div className="absolute top-[15%] right-[10%] w-12 h-12 rounded-2xl rotate-12 opacity-10" style={{ background: accent }} />
          <div className="absolute bottom-[20%] left-[10%] w-10 h-10 rounded-full opacity-10" style={{ background: accent }} />
          <div className="relative z-10 px-6">
            <p className="text-[8px] tracking-[0.4em] uppercase mb-4 font-sans font-bold" style={{ color: accent }}>Our Family</p>
            <h2 className="text-[24px] font-sans font-black text-stone-800 leading-[1.1] mb-1" style={{ fontFamily: 'system-ui' }}>Weekly</h2>
            <h2 className="text-[26px] font-sans font-black leading-[1.1] mb-3" style={{ color: accent, fontFamily: 'system-ui' }}>Organizer</h2>
            <p className="text-[9px] font-sans text-stone-500">Everyone in sync, every week</p>
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-4 flex flex-col" style={{ background: '#FFFCF5' }}>
          <h3 className="text-[10px] font-sans font-bold text-stone-700 mb-2" style={{ fontFamily: 'system-ui' }}>This Week</h3>
          <div className="flex-1">
            <div className="grid grid-cols-[60px_repeat(5,1fr)] gap-0.5 text-center">
              <div />{['Mon','Tue','Wed','Thu','Fri'].map(d => <div key={d} className="text-[6px] font-sans font-bold text-stone-400 py-1">{d}</div>)}
              {[{n:'Mom',c:'#E8612D'},{n:'Dad',c:'#4A6FA5'},{n:'Emma',c:'#E8A0BF'},{n:'Max',c:'#4CAF50'}].map((m,i) => (
                <React.Fragment key={i}>
                  <div className="text-[7px] font-sans font-bold text-stone-600 flex items-center">{m.n}</div>
                  {[...Array(5)].map((_,j) => (
                    <div key={j} className="h-8 rounded text-[5px] font-sans flex items-center justify-center" style={{ background: (i+j)%3===0 ? m.c+'15' : '#fafafa', color: m.c }}>
                      {(i+j)%3===0 && ['Soccer','Meeting','Piano','Gym','School'][(i+j)%5]}
                    </div>
                  ))}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#FFFCF5' }}>
          <h3 className="text-[10px] font-sans font-bold text-stone-700 mb-3" style={{ fontFamily: 'system-ui' }}>Chore Chart</h3>
          <div className="space-y-2 flex-1">
            {[{ch:'Dishes',who:'Emma',done:true},{ch:'Vacuum',who:'Max',done:false},{ch:'Laundry',who:'Mom',done:true},{ch:'Trash',who:'Dad',done:true},{ch:'Tidy rooms',who:'Everyone',done:false},{ch:'Cook dinner',who:'Mom & Dad',done:true}].map((c,i) => (
              <div key={i} className="flex items-center justify-between py-1 px-2 rounded-lg" style={{ background: c.done ? '#FFF3E0' : 'transparent' }}>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded border-[1.5px] flex items-center justify-center" style={{ borderColor: accent+'40', background: c.done?accent+'15':'transparent' }}>{c.done && <CheckCircle2 className="w-2.5 h-2.5" style={{ color: accent }} />}</div>
                  <span className={`text-[8px] font-sans ${c.done?'text-stone-400 line-through':'text-stone-700'}`}>{c.ch}</span>
                </div>
                <span className="text-[7px] font-sans text-stone-400">{c.who}</span>
              </div>
            ))}
          </div>
        </div>
      ),
    ],
  },

  /* ─── 23. CHRISTMAS PLANNER — Festive Red ─── */
  'Christmas Planner': {
    genType: 'daily-planner', genTopic: 'christmas holiday planner gift list menu',
    includes: '24+ pages · Festive design · Gift & menu lists',
    features: ['Gift list', 'Budget tracker', 'Menu planner', 'Decoration checklist', 'Advent calendar', 'Holiday schedule'],
    faqs: [
      { q: 'When should I start using this?', a: 'Start in October for stress-free holidays! The planner includes a countdown timeline.' },
      { q: 'Is this for other holidays too?', a: 'Primarily Christmas, but the gift list and menu sections work for any holiday celebration.' },
    ],
    reviews: [
      { name: 'Nora H.', rating: 5, text: 'Best Christmas ever! Had everything organized months in advance. No last-minute stress.', date: '2 weeks ago' },
      { name: 'Kari L.', rating: 5, text: 'The gift tracker with budget is perfect. Sold 50+ copies on Etsy in November alone!', date: '1 month ago' },
    ],
    pages: [
      ({ accent }) => (
        <div className="w-full h-full flex flex-col items-center justify-center text-center relative overflow-hidden" style={{ background: '#FDF5F5' }}>
          <div className="absolute top-[5%] left-[5%] w-6 h-6 opacity-15" style={{ color: '#2D5016' }}>&#x2742;</div>
          <div className="absolute top-[10%] right-[8%] w-4 h-4 opacity-15" style={{ color: accent }}>&#x2605;</div>
          <div className="absolute bottom-[8%] left-[12%] w-5 h-5 opacity-10" style={{ color: '#2D5016' }}>&#x2742;</div>
          <div className="relative z-10 px-6">
            <div className="text-[22px] mb-2" style={{ color: accent }}>&#x2605;</div>
            <p className="text-[8px] tracking-[0.5em] uppercase mb-4 font-serif" style={{ color: '#2D5016' }}>Holiday Season</p>
            <h2 className="text-[28px] font-serif font-bold leading-[1.1] mb-2" style={{ color: accent }}>Christmas</h2>
            <h2 className="text-[24px] font-serif italic text-stone-700 leading-[1.1] mb-3">Planner</h2>
            <div className="flex items-center justify-center gap-2 my-3"><div className="w-6 h-px" style={{ background: accent+'40' }} /><div className="w-2 h-2 rounded-full" style={{ background: '#2D5016' }} /><div className="w-6 h-px" style={{ background: accent+'40' }} /></div>
            <p className="text-[9px] font-serif italic text-stone-400">Make this Christmas magical</p>
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#FFFAFA' }}>
          <h3 className="text-xs font-serif font-bold text-stone-700 mb-3">Gift List</h3>
          <div className="space-y-1.5 flex-1">
            {[{n:'Mom',g:'Cashmere scarf',b:'$80',s:true},{n:'Dad',g:'Whisky set',b:'$65',s:true},{n:'Sister',g:'Jewelry box',b:'$45',s:false},{n:'Brother',g:'Headphones',b:'$120',s:false},{n:'Best friend',g:'Candle set',b:'$35',s:true},{n:'Teacher',g:'Gift card',b:'$25',s:false}].map((g,i) => (
              <div key={i} className="flex items-center justify-between py-1.5 px-2 rounded-lg" style={{ background: g.s ? '#F0FFF0' : accent+'06' }}>
                <div><p className="text-[8px] font-sans font-bold text-stone-700">{g.n}</p><p className="text-[7px] font-sans text-stone-400">{g.g}</p></div>
                <div className="flex items-center gap-2"><span className="text-[8px] font-sans font-bold" style={{ color: accent }}>{g.b}</span>{g.s && <CheckCircle2 className="w-3 h-3 text-emerald-500" />}</div>
              </div>
            ))}
          </div>
          <div className="mt-2 pt-2 border-t border-stone-100 flex justify-between"><span className="text-[8px] font-sans text-stone-400">Budget: $450</span><span className="text-[8px] font-sans font-bold" style={{ color: accent }}>Spent: $370</span></div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#FFFAFA' }}>
          <h3 className="text-xs font-serif font-bold text-stone-700 mb-3">Christmas Menu</h3>
          <div className="space-y-3 flex-1">
            {[{cat:'Starters',items:['Smoked salmon','Shrimp cocktail','Brie & cranberry']},{cat:'Main Course',items:['Roast turkey','Glazed ham','Roasted vegetables']},{cat:'Desserts',items:['Yule log','Gingerbread','Apple pie']}].map((c,i) => (
              <div key={i}>
                <p className="text-[7px] font-sans font-bold uppercase tracking-wider mb-1" style={{ color: accent }}>{c.cat}</p>
                {c.items.map((item,j) => (
                  <div key={j} className="flex items-center gap-1.5 py-0.5"><span className="text-[6px]" style={{ color: '#2D5016' }}>&#x25CF;</span><span className="text-[8px] font-sans text-stone-600">{item}</span></div>
                ))}
              </div>
            ))}
          </div>
        </div>
      ),
    ],
  },

  /* ─── 24. HIGH-PROTEIN MEAL PLAN — Fresh Green ─── */
  'High-Protein Meal Plan': {
    genType: 'daily-planner', genTopic: 'high protein meal plan macro tracker',
    includes: '16+ pages · Macro tracker · Shopping list',
    features: ['Weekly meal plan', 'Macro calculator', 'Shopping list', 'Meal prep guide', 'Protein sources', 'Recipe cards'],
    faqs: [
      { q: 'What calorie range is this for?', a: 'Customizable! The template includes formulas to calculate your specific macro needs.' },
      { q: 'Is this for bodybuilders?', a: 'For anyone wanting more protein — fitness enthusiasts, weight loss, or just healthier eating.' },
    ],
    reviews: [
      { name: 'Tom F.', rating: 5, text: 'Hit 150g protein daily without supplements. The meal prep section is brilliant.', date: '1 week ago' },
      { name: 'Sara N.', rating: 5, text: 'Love the macro breakdown per meal. Makes tracking so easy. Great Etsy sor!', date: '3 weeks ago' },
    ],
    pages: [
      ({ accent }) => (
        <div className="w-full h-full flex flex-col items-center justify-center text-center relative overflow-hidden" style={{ background: 'linear-gradient(155deg, #F0FAF0 0%, #E0F0E0 100%)' }}>
          <div className="absolute top-[20%] right-[15%] w-16 h-16 rounded-full opacity-[0.06]" style={{ background: accent }} />
          <div className="relative z-10 px-6">
            <p className="text-[9px] tracking-[0.4em] uppercase mb-4 font-sans font-bold" style={{ color: accent }}>Fuel Your Body</p>
            <h2 className="text-[24px] font-sans font-black text-stone-800 leading-[1.1] mb-1" style={{ fontFamily: 'system-ui' }}>High-Protein</h2>
            <h2 className="text-[26px] font-sans font-black leading-[1.05] mb-3" style={{ color: accent, fontFamily: 'system-ui' }}>Meal Plan</h2>
            <div className="flex gap-3 justify-center mt-4">
              {[{l:'Protein',v:'150g'},{l:'Carbs',v:'200g'},{l:'Fat',v:'60g'}].map((m,i) => (
                <div key={i} className="text-center"><p className="text-[10px] font-sans font-black" style={{ color: accent }}>{m.v}</p><p className="text-[6px] font-sans text-stone-400">{m.l}</p></div>
              ))}
            </div>
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#F8FFF8' }}>
          <h3 className="text-[10px] font-sans font-bold text-stone-700 mb-3" style={{ fontFamily: 'system-ui' }}>Monday Meals</h3>
          <div className="space-y-2 flex-1">
            {[{m:'Breakfast',f:'Greek yogurt bowl + granola',p:'35g',c:450},{m:'Snack',f:'Protein shake + banana',p:'30g',c:280},{m:'Lunch',f:'Chicken breast + rice + veggies',p:'45g',c:550},{m:'Snack',f:'Cottage cheese + almonds',p:'20g',c:200},{m:'Dinner',f:'Salmon + sweet potato',p:'40g',c:480}].map((meal,i) => (
              <div key={i} className="flex items-center justify-between py-1.5 px-2 rounded-lg" style={{ background: i%2===0?accent+'06':'transparent' }}>
                <div><p className="text-[7px] font-sans font-bold" style={{ color: accent }}>{meal.m}</p><p className="text-[8px] font-sans text-stone-600">{meal.f}</p></div>
                <div className="text-right"><p className="text-[8px] font-sans font-bold" style={{ color: accent }}>{meal.p}</p><p className="text-[6px] font-sans text-stone-400">{meal.c} kcal</p></div>
              </div>
            ))}
          </div>
          <div className="mt-2 pt-2 border-t" style={{ borderColor: accent+'15' }}>
            <div className="flex justify-between"><span className="text-[8px] font-sans text-stone-400">Daily total</span><span className="text-[9px] font-sans font-bold" style={{ color: accent }}>170g protein · 1,960 kcal</span></div>
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#F8FFF8' }}>
          <h3 className="text-[10px] font-sans font-bold text-stone-700 mb-3" style={{ fontFamily: 'system-ui' }}>Shopping List</h3>
          <div className="grid grid-cols-2 gap-x-3 gap-y-2 flex-1">
            {[{cat:'Protein',items:['Chicken breast 1kg','Salmon fillets','Greek yogurt','Eggs (24)']},{cat:'Carbs',items:['Brown rice','Sweet potatoes','Oats','Whole grain bread']},{cat:'Veggies',items:['Broccoli','Spinach','Bell peppers','Avocados']},{cat:'Extras',items:['Olive oil','Almonds','Protein powder','Bananas']}].map((c,i) => (
              <div key={i}>
                <p className="text-[7px] font-sans font-bold uppercase tracking-wider mb-1" style={{ color: accent }}>{c.cat}</p>
                {c.items.map((item,j) => (<div key={j} className="flex items-center gap-1 py-0.5"><div className="w-2 h-2 rounded-sm border" style={{ borderColor: accent+'30' }} /><span className="text-[7px] font-sans text-stone-600">{item}</span></div>))}
              </div>
            ))}
          </div>
        </div>
      ),
    ],
  },

  /* ─── 25. DATE NIGHT PLANNER — Dark Romantic ─── */
  'Date Night Planner': {
    genType: 'daily-planner', genTopic: 'date night planner couples bucket list',
    includes: '18+ pages · Dark romantic · 50+ date ideas',
    features: ['Date idea generator', 'Budget planner', 'Bucket list', 'Memory journal', 'Anniversary tracker', 'Rating system'],
    faqs: [
      { q: 'How many date ideas are included?', a: '50+ unique date ideas across categories: adventure, foodie, budget-friendly, stay-at-home, and seasonal.' },
      { q: 'Is this just for new couples?', a: 'Perfect for any couple! Whether dating 3 months or married 30 years.' },
    ],
    reviews: [
      { name: 'Rebecca & James', rating: 5, text: 'We do a new date every week now! The idea cards are so creative. Love it.', date: '1 week ago' },
      { name: 'Lauren P.', rating: 5, text: 'Gorgeous romantic design. My best-selling product for Valentine\'s season.', date: '3 weeks ago' },
    ],
    pages: [
      ({ accent }) => (
        <div className="w-full h-full flex flex-col items-center justify-center text-center relative overflow-hidden" style={{ background: '#1A1520' }}>
          <div className="absolute top-[30%] left-1/2 -translate-x-1/2 w-[60%] aspect-square rounded-full opacity-[0.06]" style={{ background: accent }} />
          <div className="absolute bottom-0 left-0 right-0 h-[2px]" style={{ background: `linear-gradient(90deg, transparent, ${accent}60, transparent)` }} />
          <div className="relative z-10 px-6">
            <div className="text-[20px] mb-3" style={{ color: accent }}>&#x2661;</div>
            <p className="text-[8px] tracking-[0.5em] uppercase mb-4 font-serif" style={{ color: accent }}>For Two</p>
            <h2 className="text-[26px] font-serif italic text-white leading-[1.15] mb-1">Date Night</h2>
            <h2 className="text-[24px] font-serif font-bold leading-[1.1] mb-3" style={{ color: accent }}>Planner</h2>
            <p className="text-[9px] font-sans text-stone-500">50+ ideas to keep the spark alive</p>
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#1A1520' }}>
          <h3 className="text-xs font-serif font-bold text-white mb-3">Date Ideas</h3>
          <div className="space-y-1.5 flex-1">
            {[{id:'Sunset picnic',cat:'Outdoor',b:'$'},{id:'Cooking class',cat:'Foodie',b:'$$'},{id:'Stargazing',cat:'Romantic',b:'Free'},{id:'Wine tasting',cat:'Foodie',b:'$$'},{id:'Movie marathon',cat:'Stay-in',b:'Free'},{id:'Escape room',cat:'Adventure',b:'$$'}].map((d,i) => (
              <div key={i} className="flex items-center justify-between py-1.5 px-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.04)' }}>
                <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full" style={{ background: accent }} /><span className="text-[8px] font-sans text-stone-300">{d.id}</span></div>
                <div className="flex gap-2"><span className="text-[7px] font-sans text-stone-500">{d.cat}</span><span className="text-[7px] font-sans font-bold" style={{ color: accent }}>{d.b}</span></div>
              </div>
            ))}
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#1A1520' }}>
          <h3 className="text-xs font-serif font-bold text-white mb-3">Couple Bucket List</h3>
          <div className="space-y-1.5 flex-1">
            {[{b:'Watch the sunrise together',done:true},{b:'Take a spontaneous road trip',done:true},{b:'Learn to dance',done:false},{b:'Write love letters',done:true},{b:'Visit 5 new restaurants',done:false},{b:'Go camping under the stars',done:false},{b:'Create a time capsule',done:false}].map((b,i) => (
              <div key={i} className="flex items-center gap-2 py-1">
                <div className="w-3 h-3 rounded-full border-[1.5px] flex-shrink-0 flex items-center justify-center" style={{ borderColor: accent+'50', background: b.done?accent+'20':'transparent' }}>
                  {b.done && <CheckCircle2 className="w-2.5 h-2.5" style={{ color: accent }} />}
                </div>
                <span className={`text-[8px] font-sans ${b.done?'text-stone-500 line-through':'text-stone-300'}`}>{b.b}</span>
              </div>
            ))}
          </div>
        </div>
      ),
    ],
  },

  /* ─── 26. ADHD FOCUS PLANNER — Bold Orange / Energetic ─── */
  'ADHD Focus Planner': {
    genType: 'daily-planner', genTopic: 'ADHD focus planner executive function',
    includes: '22+ pages · ADHD-friendly · Time blocking',
    features: ['Time blocking', 'Energy tracker', 'Brain dump pages', 'Dopamine menu', 'Cleaning sprints', 'Focus timer log'],
    faqs: [
      { q: 'Is this designed by someone with ADHD?', a: 'Yes! Created with ADHD coaching principles. Short tasks, visual cues, and dopamine-friendly design.' },
      { q: 'How is this different from regular planners?', a: 'Shorter time blocks, built-in breaks, energy tracking, and no overwhelming blank pages.' },
    ],
    reviews: [
      { name: 'Sam K.', rating: 5, text: 'Finally a planner that works for my ADHD brain! The time blocking is a game changer.', date: '3 days ago' },
      { name: 'Amy R.', rating: 5, text: 'The dopamine menu and cleaning sprints are genius. My most productive month ever!', date: '2 weeks ago' },
    ],
    pages: [
      ({ accent }) => (
        <div className="w-full h-full flex flex-col items-center justify-center text-center relative overflow-hidden" style={{ background: '#FFF8E0' }}>
          <div className="absolute top-[10%] left-[5%] w-10 h-10 rounded-xl rotate-12 opacity-15" style={{ background: accent }} />
          <div className="absolute bottom-[15%] right-[8%] w-8 h-8 rounded-full opacity-15" style={{ background: '#FFD600' }} />
          <div className="absolute top-[50%] left-[80%] w-6 h-6 rounded-lg rotate-[-20deg] opacity-10" style={{ background: accent }} />
          <div className="relative z-10 px-6">
            <p className="text-[9px] tracking-[0.4em] uppercase mb-4 font-sans font-black" style={{ color: accent }}>Your Brain, Your Way</p>
            <h2 className="text-[28px] font-sans font-black text-stone-800 leading-[1.05] mb-1" style={{ fontFamily: 'system-ui' }}>ADHD Focus</h2>
            <h2 className="text-[24px] font-sans font-black leading-[1.1] mb-3" style={{ color: accent, fontFamily: 'system-ui' }}>Planner</h2>
            <p className="text-[9px] font-sans text-stone-500">Designed for how your brain actually works</p>
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#FFFDF0' }}>
          <h3 className="text-xs font-sans font-black text-stone-700 mb-3" style={{ fontFamily: 'system-ui' }}>Time Blocks</h3>
          <div className="space-y-1 flex-1">
            {[{t:'9:00',task:'Morning routine',e:'High',dur:'30 min',c:accent},{t:'9:30',task:'Focus work (task 1)',e:'High',dur:'25 min',c:accent},{t:'10:00',task:'Break + movement',e:'Rest',dur:'10 min',c:'#4CAF50'},{t:'10:10',task:'Focus work (task 2)',e:'Med',dur:'25 min',c:'#FFB800'},{t:'10:35',task:'Snack + dopamine',e:'Rest',dur:'15 min',c:'#4CAF50'},{t:'10:50',task:'Admin / emails',e:'Low',dur:'20 min',c:'#90A4AE'},{t:'11:10',task:'Cleaning sprint',e:'Med',dur:'15 min',c:'#FFB800'}].map((b,i) => (
              <div key={i} className="flex items-center gap-2 py-1 px-2 rounded-lg" style={{ background: b.c+'08' }}>
                <span className="text-[7px] font-sans text-stone-400 w-8">{b.t}</span>
                <div className="w-1 h-4 rounded-full" style={{ background: b.c }} />
                <div className="flex-1"><p className="text-[8px] font-sans font-bold text-stone-700">{b.task}</p></div>
                <span className="text-[6px] font-sans text-stone-400">{b.dur}</span>
              </div>
            ))}
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#FFFDF0' }}>
          <h3 className="text-xs font-sans font-black text-stone-700 mb-3" style={{ fontFamily: 'system-ui' }}>Dopamine Menu</h3>
          <div className="grid grid-cols-2 gap-2 flex-1">
            {[{cat:'Quick (5 min)',items:['Dance to 1 song','Stretch','Eat a snack','Pet the dog']},{cat:'Medium (15 min)',items:['Short walk','Sketch/doodle','Watch 1 video','Play a game']},{cat:'Long (30+ min)',items:['Exercise','Cook something','Call a friend','Read a chapter']},{cat:'Free (no screen)',items:['Journaling','Puzzles','Garden','Organize drawer']}].map((g,i) => (
              <div key={i} className="p-2 rounded-xl" style={{ background: 'white', border: `1px solid ${accent}15` }}>
                <p className="text-[7px] font-sans font-bold mb-1" style={{ color: accent }}>{g.cat}</p>
                {g.items.map((item,j) => (<p key={j} className="text-[7px] font-sans text-stone-600 py-0.5">&#x2022; {item}</p>))}
              </div>
            ))}
          </div>
        </div>
      ),
    ],
  },

  /* ─── 27. READING JOURNAL — Literary Brown ─── */
  'Reading Journal': {
    genType: 'daily-planner', genTopic: 'book reading journal review tracker',
    includes: '24+ pages · Book reviews · Reading list',
    features: ['Book reviews', 'Reading tracker', 'TBR list', 'Favorite quotes', 'Rating system', 'Annual reading goal'],
    faqs: [
      { q: 'How many books can I track?', a: 'Space for 50+ book reviews. Duplicate pages in Canva for avid readers who need more.' },
      { q: 'Is there a yearly challenge?', a: 'Yes! Includes a reading challenge tracker with monthly and annual goals.' },
    ],
    reviews: [
      { name: 'Emily R.', rating: 5, text: 'As a bookworm, this is my dream journal. The quote collection pages are beautiful.', date: '1 week ago' },
      { name: 'David L.', rating: 5, text: 'Love the vintage literary design. My book club friends all bought one!', date: '2 weeks ago' },
    ],
    pages: [
      ({ accent }) => (
        <div className="w-full h-full flex flex-col items-center justify-center text-center relative" style={{ background: '#F8F3EB' }}>
          <div className="absolute inset-5 border rounded-sm" style={{ borderColor: accent+'15' }} />
          <div className="relative z-10 px-8">
            <p className="text-[8px] tracking-[0.4em] uppercase mb-5 font-serif" style={{ color: accent }}>A Reader&#39;s Companion</p>
            <h2 className="text-[28px] font-serif font-bold text-stone-800 leading-[1.1] mb-1">Reading</h2>
            <h2 className="text-[26px] font-serif italic leading-[1.1] mb-3" style={{ color: accent }}>Journal</h2>
            <div className="w-12 h-px mx-auto my-4" style={{ background: accent+'40' }} />
            <p className="text-[9px] font-serif italic text-stone-400">So many books, so little time</p>
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#FBF7F0' }}>
          <h3 className="text-xs font-serif font-bold text-stone-700 mb-3">Book Review</h3>
          <div className="p-3 rounded-xl mb-3" style={{ background: accent+'06', border: `1px solid ${accent}15` }}>
            <p className="text-[10px] font-serif font-bold text-stone-800 mb-0.5">The Midnight Library</p>
            <p className="text-[8px] font-sans text-stone-400 mb-2">Matt Haig · Fiction · 304 pages</p>
            <div className="flex gap-0.5 mb-2">{[...Array(5)].map((_,i) => <div key={i} className="w-3 h-3 rounded-full" style={{ background: i<4?accent:accent+'20' }} />)}</div>
            <p className="text-[8px] font-serif italic text-stone-500 leading-relaxed">{'"A beautiful reminder that every life has infinite possibilities. Made me cry and smile."'}</p>
          </div>
          <div className="space-y-2 flex-1">
            <p className="text-[8px] font-sans font-bold text-stone-500 uppercase tracking-wider">Favorite Quote</p>
            <div className="p-2 rounded-lg" style={{ background: accent+'06' }}>
              <p className="text-[8px] font-serif italic text-stone-600 leading-relaxed">{'"The only way to learn is to live."'}</p>
            </div>
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#FBF7F0' }}>
          <h3 className="text-xs font-serif font-bold text-stone-700 mb-3">Reading Challenge</h3>
          <div className="text-center mb-4">
            <p className="text-2xl font-serif font-bold" style={{ color: accent }}>18 / 52</p>
            <p className="text-[8px] font-sans text-stone-400">books read this year</p>
          </div>
          <div className="grid grid-cols-13 gap-0.5 flex-1">
            {[...Array(52)].map((_,i) => (
              <div key={i} className="aspect-square rounded-sm" style={{ background: i<18?accent:accent+'10' }} />
            ))}
          </div>
        </div>
      ),
    ],
  },

  /* ─── 28. MOVING CHECKLIST — Clean Blue ─── */
  'Moving Checklist': {
    genType: 'daily-planner', genTopic: 'moving day checklist house apartment planner',
    includes: '16+ pages · Timeline · Room-by-room',
    features: ['Moving timeline', 'Room-by-room lists', 'Address change tracker', 'Utility setup', 'Box inventory', 'First night kit'],
    faqs: [
      { q: 'How far in advance should I start?', a: 'The planner starts 8 weeks before moving day with weekly tasks and checklists.' },
      { q: 'Does it work for apartments too?', a: 'Yes! Works for houses, apartments, dorms — any type of move.' },
    ],
    reviews: [
      { name: 'Johan K.', rating: 5, text: 'Moved without any stress for the first time. The room-by-room packing lists are genius.', date: '6 days ago' },
      { name: 'Marie S.', rating: 5, text: 'The address change tracker saved me so much time. Didn\'t forget a single service!', date: '3 weeks ago' },
    ],
    pages: [
      ({ accent }) => (
        <div className="w-full h-full flex flex-col justify-center relative" style={{ background: '#F5F8FC' }}>
          <div className="absolute right-0 top-0 bottom-0 w-[5px]" style={{ background: accent }} />
          <div className="pl-6 pr-8 relative z-10">
            <p className="text-[8px] tracking-[0.4em] uppercase mb-4 font-sans font-bold" style={{ color: accent }}>Stress-Free</p>
            <h2 className="text-[26px] font-sans font-black text-stone-800 leading-[1.05] mb-1" style={{ fontFamily: 'system-ui' }}>Moving</h2>
            <h2 className="text-[28px] font-sans font-black leading-[1.05] mb-3" style={{ color: accent, fontFamily: 'system-ui' }}>Checklist</h2>
            <div className="w-8 h-[2px] my-4" style={{ background: accent }} />
            <p className="text-[9px] font-sans text-stone-500">From packing to unpacking, organized</p>
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#FAFCFF' }}>
          <h3 className="text-xs font-sans font-bold text-stone-700 mb-3" style={{ fontFamily: 'system-ui' }}>Moving Timeline</h3>
          <div className="space-y-2 flex-1">
            {[{w:'8 weeks',t:'Start decluttering',done:true},{w:'6 weeks',t:'Book moving company',done:true},{w:'4 weeks',t:'Start packing non-essentials',done:true},{w:'2 weeks',t:'Change address everywhere',done:false},{w:'1 week',t:'Pack remaining items',done:false},{w:'Moving day',t:'Final walkthrough & keys',done:false}].map((w,i) => (
              <div key={i} className="flex gap-2.5 items-start">
                <div className="flex flex-col items-center"><div className="w-2.5 h-2.5 rounded-full" style={{ background: w.done?'#4CAF50':accent }} />{i<5 && <div className="w-px h-3" style={{ background: accent+'20' }} />}</div>
                <div><p className="text-[7px] font-sans font-bold" style={{ color: accent }}>{w.w}</p><p className={`text-[8px] font-sans ${w.done?'text-stone-400 line-through':'text-stone-700'}`}>{w.t}</p></div>
              </div>
            ))}
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#FAFCFF' }}>
          <h3 className="text-xs font-sans font-bold text-stone-700 mb-3" style={{ fontFamily: 'system-ui' }}>Room-by-Room Packing</h3>
          <div className="space-y-2 flex-1">
            {[{r:'Kitchen',b:8,p:5},{r:'Living Room',b:5,p:3},{r:'Bedroom',b:6,p:2},{r:'Bathroom',b:3,p:3},{r:'Office',b:4,p:1},{r:'Garage',b:7,p:0}].map((r,i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-[8px] font-sans text-stone-600 w-16">{r.r}</span>
                <div className="flex-1 h-2.5 rounded-full" style={{ background: accent+'10' }}><div className="h-full rounded-full" style={{ width:`${(r.p/r.b)*100}%`, background: r.p===r.b?'#4CAF50':accent }} /></div>
                <span className="text-[7px] font-sans text-stone-400 w-10 text-right">{r.p}/{r.b} boxes</span>
              </div>
            ))}
          </div>
        </div>
      ),
    ],
  },

  /* ─── 29. DEBT PAYOFF TRACKER — Dark Gold / Motivational ─── */
  'Debt Payoff Tracker': {
    genType: 'daily-planner', genTopic: 'debt payoff snowball avalanche tracker',
    includes: '16+ pages · Snowball method · Visual progress',
    features: ['Debt snowball', 'Monthly payments', 'Interest calculator', 'Payoff timeline', 'Milestone rewards', 'Net worth tracker'],
    faqs: [
      { q: 'What is the snowball method?', a: 'Pay off smallest debts first for quick wins and motivation. The tracker guides you through each step.' },
      { q: 'Can I track student loans?', a: 'Yes! Track any type of debt — student loans, credit cards, car loans, mortgages.' },
    ],
    reviews: [
      { name: 'Mark T.', rating: 5, text: 'Paid off $12,000 in credit card debt in 14 months. The visual progress bars kept me going.', date: '1 week ago' },
      { name: 'Rachel G.', rating: 5, text: 'The dark gold design is so motivating. My Etsy customers love it.', date: '2 weeks ago' },
    ],
    pages: [
      ({ accent }) => (
        <div className="w-full h-full flex flex-col items-center justify-center text-center relative overflow-hidden" style={{ background: '#151515' }}>
          <div className="absolute top-[20%] left-1/2 -translate-x-1/2 w-[50%] aspect-square rounded-full opacity-[0.05]" style={{ background: accent }} />
          <div className="absolute bottom-0 left-0 right-0 h-[3px]" style={{ background: `linear-gradient(90deg, transparent, ${accent}, transparent)` }} />
          <div className="relative z-10 px-6">
            <p className="text-[9px] tracking-[0.5em] uppercase mb-4 font-sans font-bold" style={{ color: accent }}>Become Debt Free</p>
            <h2 className="text-[26px] font-sans font-black text-white leading-[1.05] mb-1" style={{ fontFamily: 'system-ui' }}>Debt Payoff</h2>
            <h2 className="text-[28px] font-sans font-black leading-[1.05] mb-3" style={{ color: accent, fontFamily: 'system-ui' }}>Tracker</h2>
            <p className="text-[9px] font-sans text-stone-500">Every payment brings you closer</p>
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#151515' }}>
          <h3 className="text-xs font-sans font-bold text-white mb-3" style={{ fontFamily: 'system-ui' }}>Debt Snowball</h3>
          <div className="space-y-2 flex-1">
            {[{n:'Store card',t:'$800',p:'$800',pct:100,s:'Paid!'},{n:'Credit card A',t:'$3,200',p:'$2,100',pct:66,s:'$1,100 left'},{n:'Car loan',t:'$12,000',p:'$4,800',pct:40,s:'$7,200 left'},{n:'Student loan',t:'$25,000',p:'$5,000',pct:20,s:'$20,000 left'}].map((d,i) => (
              <div key={i} className="p-2.5 rounded-xl" style={{ background: 'rgba(255,255,255,0.04)' }}>
                <div className="flex justify-between mb-1"><span className="text-[8px] font-sans font-bold text-stone-200">{d.n}</span><span className="text-[7px] font-sans" style={{ color: d.pct>=100?'#4CAF50':accent }}>{d.s}</span></div>
                <div className="h-2 rounded-full" style={{ background: 'rgba(255,255,255,0.06)' }}><div className="h-full rounded-full" style={{ width:`${d.pct}%`, background: d.pct>=100?'#4CAF50':accent }} /></div>
              </div>
            ))}
          </div>
          <div className="mt-2 pt-2 border-t border-stone-800 flex justify-between"><span className="text-[8px] font-sans text-stone-500">Total debt: $41,000</span><span className="text-[8px] font-sans font-bold" style={{ color: accent }}>Paid: $12,700</span></div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#151515' }}>
          <h3 className="text-xs font-sans font-bold text-white mb-3" style={{ fontFamily: 'system-ui' }}>Monthly Payments</h3>
          <div className="space-y-1 flex-1">
            {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m,i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-[7px] font-sans text-stone-500 w-6">{m}</span>
                <div className="flex-1 h-2.5 rounded" style={{ background: 'rgba(255,255,255,0.04)' }}><div className="h-full rounded" style={{ width: i<5?`${60+i*8}%`:'0%', background: accent }} /></div>
                {i<5 && <span className="text-[7px] font-sans font-bold" style={{ color: accent }}>${(500+i*50)}</span>}
              </div>
            ))}
          </div>
        </div>
      ),
    ],
  },

  /* ─── 30. PET HEALTH RECORD — Medical Blue / Clean ─── */
  'Pet Health Record': {
    genType: 'daily-planner', genTopic: 'pet health record veterinary tracker',
    includes: '18+ pages · Medical record · Multi-pet support',
    features: ['Vaccine record', 'Vet visit log', 'Medication tracker', 'Weight chart', 'Diet plan', 'Emergency contacts'],
    faqs: [
      { q: 'Can I track multiple pets?', a: 'Yes! Each section can be duplicated in Canva for additional pets.' },
      { q: 'What animals does this work for?', a: 'Designed for dogs and cats, but the medical sections work for any pet.' },
    ],
    reviews: [
      { name: 'Julia H.', rating: 5, text: 'So much easier than keeping paper records. My vet was impressed with how organized I am now.', date: '1 week ago' },
      { name: 'Anders P.', rating: 5, text: 'Clean medical design. Works perfectly for my two cats and dog.', date: '2 weeks ago' },
    ],
    pages: [
      ({ accent }) => (
        <div className="w-full h-full flex flex-col items-center justify-center text-center relative" style={{ background: '#F0F8FF' }}>
          <div className="absolute inset-5 border rounded-xl" style={{ borderColor: accent+'15' }} />
          <div className="relative z-10 px-6">
            <div className="w-10 h-10 mx-auto mb-3 rounded-xl flex items-center justify-center" style={{ background: accent+'10' }}>
              <span className="text-[16px]" style={{ color: accent }}>+</span>
            </div>
            <p className="text-[8px] tracking-[0.4em] uppercase mb-4 font-sans" style={{ color: accent }}>Veterinary</p>
            <h2 className="text-[26px] font-sans font-black text-stone-800 leading-[1.1] mb-1" style={{ fontFamily: 'system-ui' }}>Pet Health</h2>
            <h2 className="text-[22px] font-sans font-black leading-[1.1] mb-3" style={{ color: accent, fontFamily: 'system-ui' }}>Record</h2>
            <p className="text-[9px] font-sans text-stone-500">Complete medical history for your pet</p>
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#F8FCFF' }}>
          <h3 className="text-xs font-sans font-bold text-stone-700 mb-3" style={{ fontFamily: 'system-ui' }}>Vaccine Record</h3>
          <div className="space-y-1.5 flex-1">
            <div className="grid grid-cols-[1fr_auto_auto] gap-x-3 pb-1 border-b" style={{ borderColor: accent+'15' }}>
              <span className="text-[7px] font-sans font-bold text-stone-400 uppercase">Vaccine</span>
              <span className="text-[7px] font-sans font-bold text-stone-400 uppercase">Date</span>
              <span className="text-[7px] font-sans font-bold text-stone-400 uppercase">Next</span>
            </div>
            {[{v:'Rabies',d:'Jan 10',n:'Jan 2028'},{v:'DHPP',d:'Feb 15',n:'Feb 2027'},{v:'Bordetella',d:'Mar 20',n:'Sep 2027'},{v:'Leptospirosis',d:'Apr 5',n:'Apr 2027'},{v:'Influenza',d:'May 1',n:'Nov 2027'}].map((v,i) => (
              <div key={i} className="grid grid-cols-[1fr_auto_auto] gap-x-3 py-1.5 border-b border-stone-50">
                <span className="text-[8px] font-sans text-stone-700">{v.v}</span>
                <span className="text-[8px] font-sans text-stone-400">{v.d}</span>
                <span className="text-[7px] font-sans font-bold" style={{ color: accent }}>{v.n}</span>
              </div>
            ))}
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#F8FCFF' }}>
          <h3 className="text-xs font-sans font-bold text-stone-700 mb-3" style={{ fontFamily: 'system-ui' }}>Weight Tracker</h3>
          <div className="flex-1 flex flex-col justify-end gap-1">
            {[{m:'Jan',w:8.2},{m:'Feb',w:8.5},{m:'Mar',w:9.0},{m:'Apr',w:9.3},{m:'May',w:9.8},{m:'Jun',w:10.1}].map((d,i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-[7px] font-sans text-stone-400 w-6">{d.m}</span>
                <div className="flex-1 h-3 rounded-full" style={{ background: accent+'08' }}><div className="h-full rounded-full" style={{ width:`${d.w*8}%`, background: accent }} /></div>
                <span className="text-[7px] font-sans text-stone-500 w-10 text-right">{d.w} kg</span>
              </div>
            ))}
          </div>
        </div>
      ),
    ],
  },

  /* ─── 31. RUNNING PROGRAM — Outdoor Green / Athletic ─── */
  'Running Program': {
    genType: 'daily-planner', genTopic: 'running training program 5k half marathon',
    includes: '18+ pages · 8-week plan · Race prep',
    features: ['Training schedule', 'Pace calculator', 'Race day plan', 'Nutrition guide', 'Injury prevention', 'Progress log'],
    faqs: [
      { q: 'What distance is this for?', a: 'Includes plans for 5K, 10K, and half marathon. Choose your goal and follow the weekly schedule.' },
      { q: 'Am I too unfit to start?', a: 'The program starts with walk/run intervals. Perfect for complete beginners up to intermediate runners.' },
    ],
    reviews: [
      { name: 'Andreas M.', rating: 5, text: 'Ran my first half marathon after 8 weeks! The gradual progression was perfect.', date: '5 days ago' },
      { name: 'Kristin E.', rating: 5, text: 'Love the outdoor feel of the design. Motivating to open before every run.', date: '2 weeks ago' },
    ],
    pages: [
      ({ accent }) => (
        <div className="w-full h-full flex flex-col items-center justify-center text-center relative overflow-hidden" style={{ background: 'linear-gradient(160deg, #F0F5E8 0%, #E0EBD5 100%)' }}>
          <div className="absolute bottom-0 left-0 right-0 h-[30%] opacity-[0.04]" style={{ background: `linear-gradient(0deg, ${accent}, transparent)` }} />
          <div className="relative z-10 px-6">
            <p className="text-[9px] tracking-[0.4em] uppercase mb-4 font-sans font-bold" style={{ color: accent }}>Lace Up</p>
            <h2 className="text-[28px] font-sans font-black text-stone-800 leading-[1.05] mb-1" style={{ fontFamily: 'system-ui' }}>Running</h2>
            <h2 className="text-[26px] font-sans font-black leading-[1.05] mb-3" style={{ color: accent, fontFamily: 'system-ui' }}>Program</h2>
            <div className="flex gap-3 justify-center mt-4">
              {['5K','10K','21K'].map(d => (<span key={d} className="text-[9px] font-sans font-bold px-2.5 py-1 rounded-full" style={{ background: accent+'15', color: accent }}>{d}</span>))}
            </div>
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#F5F8F0' }}>
          <h3 className="text-xs font-sans font-bold text-stone-700 mb-3" style={{ fontFamily: 'system-ui' }}>Week 1 Schedule</h3>
          <div className="space-y-1.5 flex-1">
            {[{d:'Mon',t:'Easy run',dist:'2 km',pace:'6:30/km'},{d:'Tue',t:'Rest day',dist:'-',pace:'-'},{d:'Wed',t:'Intervals',dist:'3 km',pace:'5:45/km'},{d:'Thu',t:'Rest day',dist:'-',pace:'-'},{d:'Fri',t:'Easy run',dist:'2.5 km',pace:'6:30/km'},{d:'Sat',t:'Long run',dist:'4 km',pace:'7:00/km'},{d:'Sun',t:'Rest day',dist:'-',pace:'-'}].map((r,i) => (
              <div key={i} className="flex items-center justify-between py-1.5 px-2 rounded-lg" style={{ background: r.dist!=='-'?accent+'06':'transparent' }}>
                <div className="flex items-center gap-2"><span className="text-[7px] font-sans font-bold w-6" style={{ color: accent }}>{r.d}</span><span className="text-[8px] font-sans text-stone-700">{r.t}</span></div>
                <div className="flex gap-3">{r.dist!=='-' && <><span className="text-[7px] font-sans text-stone-400">{r.dist}</span><span className="text-[7px] font-sans font-bold" style={{ color: accent }}>{r.pace}</span></>}</div>
              </div>
            ))}
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#F5F8F0' }}>
          <h3 className="text-xs font-sans font-bold text-stone-700 mb-3" style={{ fontFamily: 'system-ui' }}>Race Day Prep</h3>
          <div className="space-y-2 flex-1">
            {[{t:'Night before',items:['Lay out clothes','Pin race number','Charge watch','Early dinner']},{t:'Morning',items:['Wake 2hrs early','Light breakfast','Warm up 10min','Arrive 45min before']},{t:'During race',items:['Start slow','Hydrate at stations','Maintain pace','Push last 2km']}].map((g,i) => (
              <div key={i} className="p-2 rounded-lg" style={{ background: accent+'06' }}>
                <p className="text-[7px] font-sans font-bold mb-1" style={{ color: accent }}>{g.t}</p>
                <div className="grid grid-cols-2 gap-0.5">{g.items.map((item,j) => (<div key={j} className="flex items-center gap-1"><div className="w-1.5 h-1.5 rounded-full" style={{ background: accent+'40' }} /><span className="text-[7px] font-sans text-stone-600">{item}</span></div>))}</div>
              </div>
            ))}
          </div>
        </div>
      ),
    ],
  },

  /* ─── 32. PREGNANCY JOURNAL — Soft Lavender ─── */
  'Pregnancy Journal': {
    genType: 'daily-planner', genTopic: 'pregnancy journal weekly tracker birth plan',
    includes: '28+ pages · Weekly updates · Birth plan',
    features: ['Weekly milestones', 'Bump tracker', 'Birth plan', 'Baby name list', 'Hospital bag checklist', 'Appointment log'],
    faqs: [
      { q: 'Does this cover the whole pregnancy?', a: 'Yes! From first trimester through birth, with weekly milestone pages and space for photos.' },
      { q: 'Is the birth plan customizable?', a: 'Completely! The birth plan section covers preferences for delivery, pain management, and postpartum.' },
    ],
    reviews: [
      { name: 'Camilla N.', rating: 5, text: 'Tracking my pregnancy week by week was so special. Now I have it as a keepsake forever.', date: '4 days ago' },
      { name: 'Anna B.', rating: 5, text: 'The hospital bag checklist saved me at 38 weeks! Beautiful lavender design.', date: '2 weeks ago' },
    ],
    pages: [
      ({ accent }) => (
        <div className="w-full h-full flex flex-col items-center justify-center text-center relative overflow-hidden" style={{ background: 'linear-gradient(170deg, #FFF5FA 0%, #F0E8F5 50%, #FFF5FA 100%)' }}>
          {[{t:15,l:20,s:6},{t:30,l:75,s:4},{t:65,l:10,s:5},{t:80,l:70,s:3}].map((d,i) => (
            <div key={i} className="absolute rounded-full opacity-10" style={{ top:`${d.t}%`,left:`${d.l}%`,width:`${d.s*5}px`,height:`${d.s*5}px`,background: accent }} />
          ))}
          <div className="relative z-10 px-6">
            <div className="text-[22px] mb-3" style={{ color: accent }}>&#x2661;</div>
            <p className="text-[8px] tracking-[0.4em] uppercase mb-4 font-sans" style={{ color: accent }}>Growing Together</p>
            <h2 className="text-[24px] font-serif text-stone-700 leading-[1.2] mb-1">Pregnancy</h2>
            <h2 className="text-[28px] font-serif font-bold italic leading-[1.1] mb-3" style={{ color: accent }}>Journal</h2>
            <p className="text-[9px] font-sans text-stone-400">40 weeks of love</p>
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#FFFAFD' }}>
          <h3 className="text-xs font-serif font-bold text-stone-700 mb-1">Week 20</h3>
          <p className="text-[8px] font-sans text-stone-400 mb-3">Baby is the size of a banana!</p>
          <div className="grid grid-cols-2 gap-2 mb-3">
            {[{l:'Weight',v:'+4 kg'},{l:'Baby size',v:'25 cm'},{l:'Symptoms',v:'Kicks!'},{l:'Mood',v:'Happy'}].map((s,i) => (
              <div key={i} className="p-2 rounded-xl" style={{ background: accent+'08' }}>
                <p className="text-[7px] font-sans text-stone-400">{s.l}</p>
                <p className="text-[9px] font-sans font-bold" style={{ color: accent }}>{s.v}</p>
              </div>
            ))}
          </div>
          <div className="space-y-2 flex-1">
            <p className="text-[8px] font-sans font-bold text-stone-500">This week I feel...</p>
            <div className="space-y-1"><div className="h-px bg-stone-100" /><div className="h-px bg-stone-100" /><div className="h-px bg-stone-100" /></div>
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#FFFAFD' }}>
          <h3 className="text-xs font-serif font-bold text-stone-700 mb-3">Hospital Bag</h3>
          <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 flex-1">
            {['ID & insurance','Birth plan copies','Phone charger','Comfortable clothes','Nursing bra','Toiletries','Baby outfit','Car seat','Blanket','Snacks','Water bottle','Camera'].map((item,i) => (
              <div key={i} className="flex items-center gap-1.5 py-1">
                <div className="w-2.5 h-2.5 rounded border flex-shrink-0" style={{ borderColor: accent+'30', background: i<7?accent+'12':'transparent' }}>{i<7 && <CheckCircle2 className="w-2.5 h-2.5" style={{ color: accent }} />}</div>
                <span className={`text-[8px] font-sans ${i<7?'text-stone-400 line-through':'text-stone-600'}`}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      ),
    ],
  },

  /* ─── 33. GARDEN PLANNER — Botanical Green ─── */
  'Garden Planner': {
    genType: 'daily-planner', genTopic: 'garden planner plant tracker seasonal guide',
    includes: '20+ pages · Seasonal guide · Plant tracker',
    features: ['Seasonal planting guide', 'Plant care tracker', 'Garden layout', 'Harvest log', 'Watering schedule', 'Seed inventory'],
    faqs: [
      { q: 'What climate zone is this for?', a: 'Fully customizable! The seasonal guide section lets you fill in planting dates for your specific zone.' },
      { q: 'Can I use this for indoor plants?', a: 'Yes! The plant care tracker works for both outdoor gardens and indoor plant collections.' },
    ],
    reviews: [
      { name: 'Helen G.', rating: 5, text: 'My best garden ever! The seasonal planting guide kept me on track all year.', date: '1 week ago' },
      { name: 'Petra S.', rating: 5, text: 'Beautiful botanical design. Selling well to gardening enthusiasts on Etsy.', date: '3 weeks ago' },
    ],
    pages: [
      ({ accent }) => (
        <div className="w-full h-full flex flex-col items-center justify-center text-center relative overflow-hidden" style={{ background: '#F0F8F0' }}>
          <div className="absolute top-[8%] left-[8%] text-[28px] opacity-[0.06]" style={{ color: accent }}>&#x2698;</div>
          <div className="absolute bottom-[10%] right-[10%] text-[22px] opacity-[0.06]" style={{ color: accent }}>&#x2698;</div>
          <div className="relative z-10 px-8">
            <p className="text-[8px] tracking-[0.5em] uppercase mb-5 font-serif" style={{ color: accent }}>Grow Something</p>
            <h2 className="text-[28px] font-serif font-bold text-stone-800 leading-[1.1] mb-1">Garden</h2>
            <h2 className="text-[26px] font-serif italic leading-[1.1] mb-3" style={{ color: accent }}>Planner</h2>
            <div className="flex items-center justify-center gap-1.5 my-3"><div className="w-5 h-px" style={{ background: accent+'40' }} /><span className="text-[12px]" style={{ color: accent }}>&#x2698;</span><div className="w-5 h-px" style={{ background: accent+'40' }} /></div>
            <p className="text-[9px] font-serif italic text-stone-400">From seed to harvest</p>
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#F8FCF8' }}>
          <h3 className="text-xs font-serif font-bold text-stone-700 mb-3">Plant Tracker</h3>
          <div className="space-y-1.5 flex-1">
            {[{p:'Tomatoes',s:'Fruiting',w:'Daily',sun:'Full'},{p:'Basil',s:'Growing',w:'Daily',sun:'Full'},{p:'Lavender',s:'Blooming',w:'Weekly',sun:'Full'},{p:'Lettuce',s:'Ready',w:'Every 2 days',sun:'Partial'},{p:'Strawberries',s:'Fruiting',w:'Daily',sun:'Full'},{p:'Mint',s:'Growing',w:'Every 2 days',sun:'Partial'}].map((p,i) => (
              <div key={i} className="flex items-center justify-between py-1.5 px-2 rounded-lg" style={{ background: i%2===0?accent+'06':'transparent' }}>
                <span className="text-[8px] font-sans font-bold text-stone-700">{p.p}</span>
                <div className="flex gap-2"><span className="text-[7px] font-sans text-stone-400">{p.w}</span><span className="text-[7px] font-sans px-1.5 py-0.5 rounded-full" style={{ background: accent+'15', color: accent }}>{p.s}</span></div>
              </div>
            ))}
          </div>
        </div>
      ),
      ({ accent }) => (
        <div className="w-full h-full p-5 flex flex-col" style={{ background: '#F8FCF8' }}>
          <h3 className="text-xs font-serif font-bold text-stone-700 mb-3">Seasonal Guide</h3>
          <div className="space-y-2.5 flex-1">
            {[{s:'Spring',tasks:['Start seeds indoors','Prepare beds','Plant cool crops'],c:'#66BB6A'},{s:'Summer',tasks:['Water daily','Harvest regularly','Prune tomatoes'],c:accent},{s:'Autumn',tasks:['Plant garlic','Compost leaves','Mulch beds'],c:'#FF8A65'},{s:'Winter',tasks:['Plan next year','Order seeds','Clean tools'],c:'#78909C'}].map((season,i) => (
              <div key={i} className="p-2 rounded-lg" style={{ background: season.c+'08' }}>
                <p className="text-[7px] font-sans font-bold mb-1" style={{ color: season.c }}>{season.s}</p>
                {season.tasks.map((t,j) => (<p key={j} className="text-[7px] font-sans text-stone-600">&#x2022; {t}</p>))}
              </div>
            ))}
          </div>
        </div>
      ),
    ],
  },

};
